import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  Calendar, 
  User, 
  Building2, 
  FileText, 
  Link as LinkIcon,
  Paperclip,
  Upload,
  Trash2,
  Clock,
  Edit,
  Save,
  X,
  CheckCircle,
  History,
  Share2,
  Download,
  Send,
  UserCheck,
  AlertTriangle,
  Phone,
  Mail,
  MessageCircle,
  Video,
  MapPin,
  Timer,
  Plus,
  Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import { generarPDFGestion } from '../utils/pdfGenerator';
import { useAuth } from '../context/AuthContext';
import { RichTextDisplay } from './RichTextEditor';
import RichTextEditor from './RichTextEditor';
import { parseLocalDate, formatLocalDate } from '../utils/dateHelpers';
import DOMPurify from 'dompurify';
import './DetalleGestion.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Función para convertir HTML a texto plano
const htmlToPlainText = (html) => {
  if (!html) return '';
  
  // Si no contiene tags HTML, devolver tal cual
  if (!html.includes('<')) return html;
  
  // Crear un elemento temporal para parsear el HTML
  const temp = document.createElement('div');
  temp.innerHTML = DOMPurify.sanitize(html);
  
  // Función recursiva para procesar nodos
  const processNode = (node) => {
    let result = '';
    
    node.childNodes.forEach(child => {
      if (child.nodeType === Node.TEXT_NODE) {
        result += child.textContent;
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const tagName = child.tagName.toLowerCase();
        
        switch(tagName) {
          case 'p':
            result += processNode(child) + '\n';
            break;
          case 'br':
            result += '\n';
            break;
          case 'li':
            result += '• ' + processNode(child) + '\n';
            break;
          case 'ul':
          case 'ol':
            result += processNode(child);
            break;
          case 'h1':
          case 'h2':
          case 'h3':
            result += processNode(child) + '\n';
            break;
          case 'blockquote':
            result += '"' + processNode(child) + '"\n';
            break;
          case 'a':
            const text = processNode(child);
            const href = child.getAttribute('href');
            result += href && href !== text ? `${text} (${href})` : text;
            break;
          default:
            result += processNode(child);
        }
      }
    });
    
    return result;
  };
  
  return processNode(temp).trim();
};

const ESTADOS_GESTION = [
  'Registrado',
  'En proceso de análisis',
  'Enviado / Compartido',
  'En Revisión',
  'En espera de otra gestión',
  'Completada / Archivada'
];

const PRODUCTOS_FINALES = [
  'Informe de análisis',
  'Nota informativa',
  'Requerimiento de información',
  'Denuncia',
  'Archivo sin mérito',
  'Remisión a otra instancia',
  'Otro'
];

// Componente Modal SIMPLE sin Portal para evitar errores de removeChild
const SimpleModal = ({ children, isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      className="modal-overlay" 
      onClick={onClose}
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        zIndex: 9999,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {children}
    </div>
  );
};

// Componente Modal con Portal para modales que no tienen interacción compleja
const ModalPortal = ({ children, isOpen, onClose }) => {
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  
  if (!isOpen || !mounted) return null;
  
  return ReactDOM.createPortal(
    <div 
      className="modal-overlay" 
      onClick={onClose}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}
    >
      {children}
    </div>,
    document.body
  );
};

// Helper para calcular tiempo de gestión
const calcularTiempoGestion = (fechaCreacion, fechaCompletado = null) => {
  if (!fechaCreacion) return null;
  
  const inicio = new Date(fechaCreacion);
  const fin = fechaCompletado ? new Date(fechaCompletado) : new Date();
  const diffMs = fin - inicio;
  
  const dias = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const horas = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  let texto = '';
  if (dias > 0) texto += `${dias}d `;
  if (horas > 0 || dias > 0) texto += `${horas}h `;
  texto += `${minutos}m`;
  
  return { dias, horas, minutos, texto: texto.trim(), totalMinutos: Math.floor(diffMs / (1000 * 60)) };
};

// Componente de estrellas usando botones HTML nativos (sin estado React)
const StarRating = ({ value, onChange, size = 32 }) => {
  // Usar ref para evitar re-renders de React
  const containerRef = React.useRef(null);
  
  React.useEffect(() => {
    if (!containerRef.current) return;
    
    // Actualizar visualmente las estrellas sin re-render de React
    const stars = containerRef.current.querySelectorAll('.star-item');
    stars.forEach((star, index) => {
      star.style.color = index < value ? '#FFB800' : '#E0E0E0';
    });
  }, [value]);
  
  const handleStarClick = (starValue) => {
    if (onChange) {
      onChange(starValue);
    }
  };
  
  return (
    <div 
      ref={containerRef}
      style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className="star-item"
          onClick={() => handleStarClick(star)}
          onMouseEnter={(e) => { e.target.style.transform = 'scale(1.2)'; }}
          onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; }}
          style={{
            cursor: 'pointer',
            fontSize: `${size}px`,
            color: star <= value ? '#FFB800' : '#E0E0E0',
            transition: 'transform 0.15s ease, color 0.15s ease',
            userSelect: 'none',
            lineHeight: 1,
            padding: '4px',
            display: 'inline-block'
          }}
        >
          ★
        </span>
      ))}
    </div>
  );
};

const DetalleGestion = ({ gestionId, onBack }) => {
  const { 
    isViewer, 
    canPerformActions, 
    isAnalyst,
    isAdmin,
    canRegisterAvance,
    canGrantProrroga,
    canCalificarInstancia,
    canMarkSeguimiento,
    canMarkImprocedente,
    canAnalisisComplementario
  } = useAuth();
  
  // Variables de permisos
  const puedeRealizarAcciones = canPerformActions() && !isViewer(); // Viewer NO puede realizar acciones
  const esAnalista = isAnalyst();
  // Permisos específicos para funciones de Analista (Editor NO puede)
  const puedeRegistrarAvance = canRegisterAvance();
  const puedeOtorgarProrroga = canGrantProrroga();
  const puedeCalificarInstancia = canCalificarInstancia();
  const puedeMarcarSeguimiento = canMarkSeguimiento();
  const puedeMarcarImprocedente = canMarkImprocedente();
  const puedeAnalisisComp = canAnalisisComplementario();
  
  const [gestion, setGestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [nuevoAvance, setNuevoAvance] = useState('');
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [linkOneDrive, setLinkOneDrive] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [indicadores, setIndicadores] = useState([]);
  const [asesorProbidadInfo, setAsesorProbidadInfo] = useState(null);
  
  // Estados para derivación a Probidad (Fase 2)
  const [showDerivarModal, setShowDerivarModal] = useState(false);
  const [asesoresProbidad, setAsesoresProbidad] = useState([]);
  const [selectedAsesorProbidad, setSelectedAsesorProbidad] = useState('');
  const [comentarioDerivacion, setComentarioDerivacion] = useState('');
  const [fechaAsignacionProbidad, setFechaAsignacionProbidad] = useState(new Date().toISOString().split('T')[0]);
  
  // Estados para acciones de seguimiento (Mejora 2)
  const [accionesSeguimiento, setAccionesSeguimiento] = useState({
    llamadas: 0,
    correos: 0,
    whatsapp: 0,
    reuniones: 0,
    visitas: 0
  });
  const [guardandoAcciones, setGuardandoAcciones] = useState(false);
  
  // Estados para historial de análisis (Mejora análisis complementario)
  const [nuevoAnalisis, setNuevoAnalisis] = useState('');
  const [editandoAnalisis, setEditandoAnalisis] = useState(null);
  const [guardandoAnalisis, setGuardandoAnalisis] = useState(false);
  const [fechaVencimientoProbidad, setFechaVencimientoProbidad] = useState('');

  // Estados para calificación de Instancia de Probidad
  const [showCalificarModal, setShowCalificarModal] = useState(false);
  const [calificacion, setCalificacion] = useState(0);
  const [hoverCalificacion, setHoverCalificacion] = useState(0);
  const [observacionCalificacion, setObservacionCalificacion] = useState('');
  const [productoFinal, setProductoFinal] = useState('');
  // Checklist de evaluación de Probidad (5 preguntas)
  const [checklistProbidad, setChecklistProbidad] = useState({
    dentro_plazo: null,
    averiguacion_caso: null,
    respuesta_tecnica: null,
    producto_coherente: null,
    actuacion_objetiva: null
  });
  const [overrideCalificacion, setOverrideCalificacion] = useState(false);

  // Estados para edición de Probidad por Admin
  const [editandoProbidad, setEditandoProbidad] = useState(false);
  const [editAsesorProbidadId, setEditAsesorProbidadId] = useState('');
  const [editFechaDerivacion, setEditFechaDerivacion] = useState('');
  const [editFechaVencProbidad, setEditFechaVencProbidad] = useState('');
  const [guardandoEditProbidad, setGuardandoEditProbidad] = useState(false);

  // Estado para Análisis Complementario
  const [analisisComplementario, setAnalisisComplementario] = useState('');
  const [historialAnalisis, setHistorialAnalisis] = useState([]);
  const [editandoAnalisisIndex, setEditandoAnalisisIndex] = useState(null);
  const [textoEdicionAnalisis, setTextoEdicionAnalisis] = useState('');

  // Estado para ver calificaciones
  const [showVerCalificacionModal, setShowVerCalificacionModal] = useState(false);

  // Estado para prórroga
  const [showProrrogaModal, setShowProrrogaModal] = useState(false);
  const [diasProrroga, setDiasProrroga] = useState(15);
  const [motivoProrroga, setMotivoProrroga] = useState('');

  // Estado para editar prórroga existente
  const [showEditProrrogaModal, setShowEditProrrogaModal] = useState(false);
  const [editDiasProrroga, setEditDiasProrroga] = useState(0);
  const [editMotivoProrroga, setEditMotivoProrroga] = useState('');

  // Estado para marcar improcedente
  const [showImprocedeneModal, setShowImprocedenteModal] = useState(false);
  const [motivoImprocedente, setMotivoImprocedente] = useState('');

  // Estado para caso urgente
  const [guardandoUrgente, setGuardandoUrgente] = useState(false);

  useEffect(() => {
    if (gestionId) {
      fetchGestion();
      fetchIndicadores();
    }
  }, [gestionId]);

  // Auto-calcular calificación sugerida basada en el checklist
  useEffect(() => {
    if (!overrideCalificacion) {
      const respuestas = Object.values(checklistProbidad);
      const respondidas = respuestas.filter(v => v !== null);
      if (respondidas.length > 0) {
        const positivas = respondidas.filter(v => v === true).length;
        // Mapear: 0 respondidas positivas sobre total = proporcional a 1-5
        const calculada = Math.max(1, Math.round((positivas / 5) * 5));
        setCalificacion(calculada);
      }
    }
  }, [checklistProbidad, overrideCalificacion]);


  const fetchIndicadores = async () => {
    try {
      const response = await axios.get(`${API}/catalogos/indicadores-banderas-rojas`);
      setIndicadores(response.data);
    } catch (error) {
      console.error('Error al cargar indicadores:', error);
    }
  };

  // Cargar información de contacto del asesor de probidad
  const fetchAsesorProbidadInfo = async (asesorId) => {
    if (!asesorId) return;
    try {
      const response = await axios.get(`${API}/asignacion/asesores-probidad`);
      const asesor = response.data.find(a => a.id === asesorId);
      if (asesor) {
        setAsesorProbidadInfo(asesor);
      }
    } catch (error) {
      console.error('Error al cargar info del asesor de probidad:', error);
    }
  };

  const fetchGestion = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/gestiones/${gestionId}`);
      setGestion(response.data);
      setNuevoEstado(response.data.estado || 'Registrado');
      // Cargar análisis complementario si existe
      setAnalisisComplementario(response.data.analisis_complementario || '');
      // Cargar historial de análisis si existe
      setHistorialAnalisis(response.data.historial_analisis || []);
      // Cargar acciones de seguimiento si existen
      if (response.data.acciones_seguimiento) {
        setAccionesSeguimiento(response.data.acciones_seguimiento);
      }
      // Cargar info del asesor de probidad si existe
      if (response.data.asesor_probidad_id) {
        fetchAsesorProbidadInfo(response.data.asesor_probidad_id);
      }
    } catch (error) {
      console.error('Error al cargar gestión:', error);
      toast.error('Error al cargar el detalle de la gestión');
    } finally {
      setLoading(false);
    }
  };
  
  // Guardar acciones de seguimiento (Mejora 2 - Rol Analista)
  const handleGuardarAccionesSeguimiento = async () => {
    if (!gestion?.asesor_probidad_nombre) {
      toast.error('Las acciones de seguimiento solo están disponibles después de derivar a Probidad');
      return;
    }
    
    setGuardandoAcciones(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/gestiones/${gestionId}/acciones-seguimiento`,
        accionesSeguimiento,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Acciones de seguimiento guardadas');
      fetchGestion(); // Recargar para actualizar bitácora
    } catch (error) {
      console.error('Error al guardar acciones:', error);
      toast.error('Error al guardar las acciones de seguimiento');
    } finally {
      setGuardandoAcciones(false);
    }
  };
  
  // Agregar nuevo análisis complementario
  const handleAgregarAnalisis = async () => {
    if (!nuevoAnalisis.trim()) {
      toast.error('Escriba el texto del análisis');
      return;
    }
    
    setGuardandoAnalisis(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/gestiones/${gestionId}/analisis-complementario`,
        { texto: nuevoAnalisis },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Análisis agregado correctamente');
      setNuevoAnalisis('');
      fetchGestion(); // Recargar para ver el historial actualizado
    } catch (error) {
      console.error('Error al agregar análisis:', error);
      toast.error('Error al guardar el análisis');
    } finally {
      setGuardandoAnalisis(false);
    }
  };
  
  // Editar análisis existente
  const handleEditarAnalisis = async (index, nuevoTexto) => {
    if (!nuevoTexto.trim()) {
      toast.error('El texto del análisis no puede estar vacío');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/gestiones/${gestionId}/analisis-complementario/${index}`,
        { texto: nuevoTexto },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Análisis actualizado');
      setEditandoAnalisis(null);
      fetchGestion();
    } catch (error) {
      console.error('Error al editar análisis:', error);
      toast.error('Error al actualizar el análisis');
    }
  };

  // Cargar asesores de probidad filtrados por institución
  const fetchAsesoresProbidad = async () => {
    if (!gestion?.institucion) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/asignacion/asesores-probidad-por-institucion/${encodeURIComponent(gestion.institucion)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAsesoresProbidad(response.data.asesores || []);
    } catch (error) {
      console.error('Error al cargar asesores de probidad:', error);
    }
  };

  // Marcar como procedente (habilita derivación)
  const handleMarcarProcedente = async (esProcedente) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/asignacion/gestiones/${gestionId}/marcar-procedente?procedente=${esProcedente}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(esProcedente ? 'Gestión marcada como PROCEDENTE' : 'Gestión archivada como IMPROCEDENTE');
      fetchGestion();
    } catch (error) {
      toast.error('Error al marcar procedencia');
    }
  };

  // Derivar a asesor de probidad (Fase 2)
  const handleDerivarProbidad = async () => {
    if (!selectedAsesorProbidad) {
      toast.error('Seleccione un asesor de probidad');
      return;
    }
    if (!fechaAsignacionProbidad) {
      toast.error('La fecha de derivación es obligatoria');
      return;
    }
    if (!fechaVencimientoProbidad) {
      toast.error('La fecha de vencimiento es obligatoria');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      
      // Construir URL con parámetros
      const params = new URLSearchParams({
        asesor_probidad_id: selectedAsesorProbidad,
        comentario: comentarioDerivacion,
        fecha_asignacion_probidad: fechaAsignacionProbidad,
        fecha_vencimiento_probidad: fechaVencimientoProbidad
      });
      
      await axios.post(
        `${API}/asignacion/gestiones/${gestionId}/derivar-probidad?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Gestión derivada a Instancia de Probidad');
      setShowDerivarModal(false);
      setSelectedAsesorProbidad('');
      setComentarioDerivacion('');
      setFechaAsignacionProbidad(new Date().toISOString().split('T')[0]);
      setFechaVencimientoProbidad('');
      fetchGestion();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al derivar gestión');
    }
  };

  // Editar información de Probidad (Solo Admin)
  const handleEditarProbidad = () => {
    setEditAsesorProbidadId(gestion.asesor_probidad_id || '');
    setEditFechaDerivacion(gestion.fecha_asignacion_probidad?.split('T')[0] || '');
    setEditFechaVencProbidad(gestion.fecha_vencimiento_probidad?.split('T')[0] || '');
    setEditandoProbidad(true);
    fetchAsesoresProbidad();
  };

  const handleGuardarEditProbidad = async () => {
    if (!editAsesorProbidadId) {
      toast.error('Seleccione un asesor de probidad');
      return;
    }
    if (!editFechaDerivacion) {
      toast.error('La fecha de derivación es obligatoria');
      return;
    }
    if (!editFechaVencProbidad) {
      toast.error('La fecha de vencimiento es obligatoria');
      return;
    }
    
    setGuardandoEditProbidad(true);
    try {
      const token = localStorage.getItem('token');
      
      // Obtener nombre del asesor seleccionado
      const asesorSeleccionado = asesoresProbidad.find(a => a.id === editAsesorProbidadId);
      
      await axios.patch(
        `${API}/gestiones/${gestionId}/editar-probidad`,
        {
          asesor_probidad_id: editAsesorProbidadId,
          asesor_probidad_nombre: asesorSeleccionado?.nombre_completo || gestion.asesor_probidad_nombre,
          fecha_asignacion_probidad: editFechaDerivacion,
          fecha_vencimiento_probidad: editFechaVencProbidad
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Información de Probidad actualizada');
      setEditandoProbidad(false);
      fetchGestion();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar información');
    } finally {
      setGuardandoEditProbidad(false);
    }
  };

  // Completar gestión con calificación
  const handleCompletarGestion = async () => {
    // Validar checklist completado si tiene asesor de probidad
    if (gestion.asesor_probidad_nombre) {
      const respuestas = Object.values(checklistProbidad);
      const todasRespondidas = respuestas.every(v => v !== null);
      if (!todasRespondidas) {
        toast.error('Debe completar todas las preguntas del checklist de evaluación');
        return;
      }
      if (calificacion === 0) {
        toast.error('Por favor califique a la Instancia de Probidad');
        return;
      }
      // Observaciones obligatorias si calificación <= 3
      if (calificacion <= 3 && !observacionCalificacion.trim()) {
        toast.error('Las observaciones son obligatorias cuando la calificación es 3 o menor');
        return;
      }
    }
    if (!productoFinal) {
      toast.error('Seleccione el producto final obtenido');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const userEmail = localStorage.getItem('userEmail') || 'usuario';
      
      // Preparar datos de calificación para guardar en la gestión
      let calificacionData = null;
      
      // Primero guardar calificación si hay asesor de probidad
      if (gestion.asesor_probidad_nombre && calificacion > 0) {
        await axios.post(
          `${API}/calificaciones/instancia-gestion`,
          {
            gestion_id: gestionId,
            institucion: gestion.institucion,
            asesor_probidad_nombre: gestion.asesor_probidad_nombre,
            calificacion: calificacion,
            observacion: observacionCalificacion,
            checklist: checklistProbidad,
            override_manual: overrideCalificacion
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Guardar calificación en el objeto de gestión
        calificacionData = {
          puntuacion: calificacion,
          comentario: observacionCalificacion,
          checklist: checklistProbidad,
          override_manual: overrideCalificacion,
          fecha: new Date().toISOString(),
          usuario: userEmail
        };
      }

      // Cambiar estado a Completada/Archivada
      await axios.post(
        `${API}/gestiones/${gestionId}/avances`,
        {
          texto: `Gestión completada. Producto final: ${productoFinal}`,
          nuevo_estado: 'Completada / Archivada'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Actualizar producto obtenido y calificación
      const updateData = { producto_obtenido: productoFinal };
      if (calificacionData) {
        updateData.calificacion_instancia = calificacionData;
      }
      
      await axios.put(
        `${API}/gestiones/${gestionId}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Gestión completada exitosamente');
      setShowCalificarModal(false);
      setCalificacion(0);
      setObservacionCalificacion('');
      setProductoFinal('');
      setChecklistProbidad({ dentro_plazo: null, averiguacion_caso: null, respuesta_tecnica: null, producto_coherente: null, actuacion_objetiva: null });
      setOverrideCalificacion(false);
      fetchGestion();
    } catch (error) {
      toast.error('Error al completar la gestión');
    }
  };

  // Guardar análisis complementario (agrega al historial)
  const handleGuardarAnalisis = async () => {
    // Strip HTML tags for empty check
    const plainText = analisisComplementario.replace(/<[^>]*>/g, '').trim();
    if (!plainText) {
      toast.error('El análisis complementario no puede estar vacío');
      return;
    }
    
    setGuardandoAnalisis(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/gestiones/${gestionId}/analisis-complementario`,
        { texto: analisisComplementario },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Análisis complementario agregado correctamente');
      setAnalisisComplementario(''); // Limpiar para nuevo análisis
      fetchGestion();
    } catch (error) {
      console.error('Error al guardar análisis:', error);
      toast.error('Error al guardar el análisis complementario');
    } finally {
      setGuardandoAnalisis(false);
    }
  };

  // Iniciar edición de un análisis existente
  const handleIniciarEdicionAnalisis = (index) => {
    setEditandoAnalisisIndex(index);
    setTextoEdicionAnalisis(historialAnalisis[index].texto);
  };

  // Cancelar edición
  const handleCancelarEdicionAnalisis = () => {
    setEditandoAnalisisIndex(null);
    setTextoEdicionAnalisis('');
  };

  // Guardar edición de análisis
  const handleGuardarEdicionAnalisis = async () => {
    if (!textoEdicionAnalisis.trim()) {
      toast.error('El análisis no puede estar vacío');
      return;
    }
    
    setGuardandoAnalisis(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/gestiones/${gestionId}/analisis-complementario/${editandoAnalisisIndex}`,
        { texto: textoEdicionAnalisis },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Análisis editado correctamente');
      setEditandoAnalisisIndex(null);
      setTextoEdicionAnalisis('');
      fetchGestion();
    } catch (error) {
      console.error('Error al editar análisis:', error);
      toast.error('Error al editar el análisis');
    } finally {
      setGuardandoAnalisis(false);
    }
  };

  // Eliminar análisis del historial
  const handleEliminarAnalisis = async (index) => {
    if (!window.confirm(`¿Está seguro de eliminar el análisis #${index + 1}? Esta acción no se puede deshacer.`)) {
      return;
    }
    
    setGuardandoAnalisis(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/gestiones/${gestionId}/analisis-complementario/${index}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Análisis eliminado correctamente');
      fetchGestion();
    } catch (error) {
      console.error('Error al eliminar análisis:', error);
      toast.error('Error al eliminar el análisis');
    } finally {
      setGuardandoAnalisis(false);
    }
  };

  // Otorgar prórroga en el plazo
  const handleOtorgarProrroga = async () => {
    if (!diasProrroga || diasProrroga < 1) {
      toast.error('Debe especificar los días de prórroga');
      return;
    }
    if (!motivoProrroga.trim()) {
      toast.error('Debe especificar el motivo de la prórroga');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Calcular nueva fecha de vencimiento usando parseLocalDate para evitar problemas de timezone
      const fechaOriginal = gestion.fecha_vencimiento_probidad || gestion.fecha_vencimiento_probidad_original;
      let nuevaFechaVencimiento;
      
      if (fechaOriginal) {
        // Usar parseLocalDate para obtener la fecha correcta sin desfase de timezone
        nuevaFechaVencimiento = parseLocalDate(fechaOriginal);
        if (!nuevaFechaVencimiento) {
          nuevaFechaVencimiento = new Date();
        }
        nuevaFechaVencimiento.setDate(nuevaFechaVencimiento.getDate() + parseInt(diasProrroga));
      } else {
        nuevaFechaVencimiento = new Date();
        nuevaFechaVencimiento.setDate(nuevaFechaVencimiento.getDate() + parseInt(diasProrroga));
      }

      // Formatear la fecha para guardar en formato YYYY-MM-DD
      const year = nuevaFechaVencimiento.getFullYear();
      const month = String(nuevaFechaVencimiento.getMonth() + 1).padStart(2, '0');
      const day = String(nuevaFechaVencimiento.getDate()).padStart(2, '0');
      const nuevaFechaStr = `${year}-${month}-${day}`;

      // Actualizar la gestión con la prórroga
      await axios.put(
        `${API}/gestiones/${gestionId}`,
        {
          prorroga_probidad: parseInt(diasProrroga),
          prorroga_probidad_motivo: motivoProrroga,
          fecha_vencimiento_probidad_original: gestion.fecha_vencimiento_probidad_original || gestion.fecha_vencimiento_probidad,
          fecha_vencimiento_probidad: nuevaFechaStr
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Agregar entrada a bitácora
      await axios.post(
        `${API}/gestiones/${gestionId}/avances`,
        {
          texto: `Prórroga otorgada: ${diasProrroga} días adicionales. Motivo: ${motivoProrroga}. Nueva fecha de vencimiento: ${formatLocalDate(nuevaFechaStr)}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Prórroga de ${diasProrroga} días otorgada correctamente`);
      setShowProrrogaModal(false);
      setDiasProrroga(15);
      setMotivoProrroga('');
      fetchGestion();
    } catch (error) {
      console.error('Error al otorgar prórroga:', error);
      toast.error('Error al otorgar la prórroga');
    }
  };

  // Abrir modal de edición de prórroga
  const abrirEditarProrroga = () => {
    setEditDiasProrroga(gestion.prorroga_probidad || 0);
    setEditMotivoProrroga(gestion.prorroga_probidad_motivo || '');
    setShowEditProrrogaModal(true);
  };

  // Editar prórroga existente
  const handleEditarProrroga = async () => {
    if (!editDiasProrroga || editDiasProrroga < 1) {
      toast.error('Debe especificar los días de prórroga');
      return;
    }
    if (!editMotivoProrroga.trim()) {
      toast.error('Debe especificar el motivo de la prórroga');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Recalcular fecha desde la fecha ORIGINAL de vencimiento
      const fechaOriginal = gestion.fecha_vencimiento_probidad_original || gestion.fecha_vencimiento_probidad;
      let nuevaFechaVencimiento = parseLocalDate(fechaOriginal);
      if (!nuevaFechaVencimiento) {
        nuevaFechaVencimiento = new Date();
      }
      nuevaFechaVencimiento.setDate(nuevaFechaVencimiento.getDate() + parseInt(editDiasProrroga));

      const year = nuevaFechaVencimiento.getFullYear();
      const month = String(nuevaFechaVencimiento.getMonth() + 1).padStart(2, '0');
      const day = String(nuevaFechaVencimiento.getDate()).padStart(2, '0');
      const nuevaFechaStr = `${year}-${month}-${day}`;

      await axios.put(
        `${API}/gestiones/${gestionId}`,
        {
          prorroga_probidad: parseInt(editDiasProrroga),
          prorroga_probidad_motivo: editMotivoProrroga,
          fecha_vencimiento_probidad: nuevaFechaStr
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Registrar en bitácora
      await axios.post(
        `${API}/gestiones/${gestionId}/avances`,
        {
          texto: `Prórroga editada: ${editDiasProrroga} días (antes: ${gestion.prorroga_probidad} días). Motivo: ${editMotivoProrroga}. Nueva fecha de vencimiento: ${formatLocalDate(nuevaFechaStr)}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Prórroga actualizada correctamente');
      setShowEditProrrogaModal(false);
      fetchGestion();
    } catch (error) {
      console.error('Error al editar prórroga:', error);
      toast.error('Error al editar la prórroga');
    }
  };


  // Marcar gestión como improcedente
  const handleMarcarImprocedente = async () => {
    if (!motivoImprocedente.trim()) {
      toast.error('Debe especificar el motivo para marcar como improcedente');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/gestiones/${gestionId}/marcar-improcedente?motivo=${encodeURIComponent(motivoImprocedente)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Gestión marcada como improcedente y archivada');
      setShowImprocedenteModal(false);
      setMotivoImprocedente('');
      fetchGestion();
    } catch (error) {
      console.error('Error al marcar como improcedente:', error);
      toast.error('Error al marcar como improcedente');
    }
  };

  // Toggle caso urgente
  const handleToggleUrgente = async () => {
    setGuardandoUrgente(true);
    try {
      const token = localStorage.getItem('token');
      const nuevoEstado = !gestion.es_urgente;
      
      await axios.post(
        `${API}/gestiones/${gestionId}/toggle-urgente?es_urgente=${nuevoEstado}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(nuevoEstado ? 'Caso marcado como URGENTE' : 'Caso desmarcado como urgente');
      fetchGestion();
    } catch (error) {
      console.error('Error al cambiar estado urgente:', error);
      toast.error('Error al cambiar estado de urgencia');
    } finally {
      setGuardandoUrgente(false);
    }
  };

  // Generar texto para compartir (misma estructura que PDF)
  const generarTextoCompartir = () => {
    if (!gestion) return '';
    
    // Mapeo de etiquetas para campos específicos
    const labelMap = {
      'network': 'Red Social',
      'author': 'Autor o cuenta',
      'tematica': 'Temática relacionada',
      'persons': 'Personas relacionadas',
      'link': 'Enlace publicación',
      'color_bandera': 'Color de bandera',
      'indicador_codigo': 'Indicador',
      'unidad_compradora': 'Unidad compradora',
      'cantidad_nog': 'Cantidad de NOG',
      'cantidad_npg': 'Cantidad de NPG',
      'enlace_guatecompras': 'Enlace Guatecompras',
      'referencia': 'Referencia',
      'numero_oficio': 'Referencia',
      'fecha_solicitud': 'Fecha solicitud',
      'fecha_recepcion': 'Fecha recepción',
      'canal_recepcion': 'Canal de recepción',
      'caso_origen': 'Referencia'
    };
    
    let texto = '';
    
    // === HEADER ===
    texto += `📋 *ALERTA 360 - CNC Guatemala*\n`;
    texto += `━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
    
    // === NOMENCLATURA Y TIPO ===
    texto += `*${gestion.nomenclatura}*\n`;
    texto += `📁 ${gestion.tipo_gestion}\n\n`;
    
    // === 1. CREADO POR ===
    texto += `👤 *Creado por:* ${gestion.createdBy || 'N/A'} | ${gestion.createdAt ? new Date(gestion.createdAt).toLocaleDateString('es-ES') : 'N/A'}\n\n`;
    
    // === 2. DESCRIPCIÓN / HALLAZGO ===
    texto += `📝 *DESCRIPCIÓN / HALLAZGO*\n`;
    texto += `${htmlToPlainText(gestion.descripcion) || 'Sin descripción'}\n\n`;
    
    // === 3. INFORMACIÓN ESPECÍFICA ===
    const campos = gestion.campos_especificos || {};
    const camposSimples = Object.entries(campos).filter(
      ([key, value]) => value && !Array.isArray(value) && typeof value !== 'object' && key !== 'detalle_nogs'
    );
    
    if (camposSimples.length > 0) {
      texto += `📋 *INFORMACIÓN ESPECÍFICA*\n`;
      camposSimples.forEach(([key, value]) => {
        const label = labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        texto += `  • ${label}: ${value}\n`;
      });
      texto += '\n';
    }
    
    // === 4. ANÁLISIS COMPLEMENTARIO (Historial) ===
    const historial = gestion.historial_analisis || [];
    if (historial.length > 0) {
      texto += `📊 *ANÁLISIS COMPLEMENTARIO CNC (${historial.length})*\n`;
      historial.forEach((entrada, idx) => {
        texto += `\n  [${idx + 1}] ${new Date(entrada.fecha).toLocaleDateString('es-ES')} - ${entrada.usuario}\n`;
        texto += `  ${entrada.texto}\n`;
        if (entrada.editado_por) {
          texto += `  (Editado por ${entrada.editado_por})\n`;
        }
      });
      texto += '\n';
    } else if (gestion.analisis_complementario) {
      texto += `📊 *ANÁLISIS COMPLEMENTARIO (Asesor CNC)*\n`;
      texto += `${gestion.analisis_complementario}\n\n`;
    }
    
    // === 5. GESTIÓN ASIGNADA ===
    texto += `👥 *GESTIÓN ASIGNADA*\n`;
    texto += `  • Institución: ${gestion.institucion || 'N/A'}\n`;
    texto += `  • Asesor CNC: ${gestion.nombre_asesor_cnc || 'N/A'}\n`;
    texto += `  • Fecha asignación: ${new Date(gestion.fecha_asignacion).toLocaleDateString('es-ES')}\n`;
    if (gestion.fecha_vencimiento) {
      texto += `  • Vencimiento CNC: ${new Date(gestion.fecha_vencimiento).toLocaleDateString('es-ES')}\n`;
    }
    texto += `  • Estado: ${gestion.estado}\n\n`;
    
    // === 6. DERIVACIÓN A INSTANCIA DE PROBIDAD ===
    if (gestion.asesor_probidad_nombre) {
      texto += `🏛️ *DERIVACIÓN A INSTANCIA DE PROBIDAD*\n`;
      texto += `  • Instancia: ${gestion.asesor_probidad_nombre}\n`;
      if (gestion.fecha_asignacion_probidad || gestion.fecha_derivacion) {
        texto += `  • Fecha derivación: ${new Date(gestion.fecha_asignacion_probidad || gestion.fecha_derivacion).toLocaleDateString('es-ES')}\n`;
      }
      if (gestion.fecha_vencimiento_probidad) {
        texto += `  • Vencimiento: ${new Date(gestion.fecha_vencimiento_probidad).toLocaleDateString('es-ES')}\n`;
      }
      if (gestion.prorroga_probidad && gestion.prorroga_probidad > 0) {
        texto += `  • Prórroga: ${gestion.prorroga_probidad} días`;
        if (gestion.fecha_prorroga_otorgada) {
          texto += ` (otorgada: ${new Date(gestion.fecha_prorroga_otorgada).toLocaleDateString('es-ES')})`;
        }
        texto += '\n';
      }
      // Calificación si existe
      if (gestion.calificacion_instancia) {
        texto += `  • Calificación: ${'★'.repeat(gestion.calificacion_instancia.puntuacion)}${'☆'.repeat(5 - gestion.calificacion_instancia.puntuacion)} (${gestion.calificacion_instancia.puntuacion}/5)\n`;
        if (gestion.calificacion_instancia.observacion) {
          texto += `  • Observación: ${gestion.calificacion_instancia.observacion}\n`;
        }
      }
      texto += '\n';
    }
    
    // === 7. BITÁCORA (últimos 3 eventos) ===
    if (gestion.bitacora && gestion.bitacora.length > 0) {
      texto += `📜 *BITÁCORA (últimos eventos)*\n`;
      const ultimosEventos = gestion.bitacora.slice(-3).reverse();
      ultimosEventos.forEach(evento => {
        const fecha = new Date(evento.fecha).toLocaleDateString('es-ES');
        texto += `  📌 ${evento.accion} - ${fecha}\n`;
      });
      texto += '\n';
    }
    
    texto += `━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    texto += `_Sistema ALERTA 360 - CNC Guatemala_`;
    
    return texto;
  };

  // Manejar generación de PDF (WeasyPrint backend)
  const handleGenerarPDF = async () => {
    try {
      toast.info('Generando PDF, por favor espere...');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/gestiones/${gestionId}/exportar-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 60000
      });
      
      if (response.data.size === 0) {
        toast.error('El PDF generado está vacío');
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${gestion?.nomenclatura || 'gestion'}_Reporte.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF generado correctamente');
    } catch (error) {
      console.error('Error al generar PDF:', error);
      const msg = error.response?.status === 404 ? 'Gestión no encontrada' :
                  error.response?.status === 500 ? 'Error interno del servidor al generar PDF' :
                  error.code === 'ECONNABORTED' ? 'Timeout: el PDF tardó demasiado en generarse' :
                  'Error al generar PDF';
      toast.error(msg);
    }
  };

  // Manejar compartir nativo
  const handleCompartirNativo = async () => {
    const texto = generarTextoCompartir();
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${gestion.nomenclatura} - ${gestion.tipo_gestion}`,
          text: texto,
          url: window.location.href
        });
        toast.success('Compartido correctamente');
        setShowShareModal(false);
      } catch (error) {
        if (error.name !== 'AbortError') {
          handleCopiarTexto();
        }
      }
    } else {
      handleCopiarTexto();
    }
  };

  // Copiar texto al portapapeles
  const handleCopiarTexto = async () => {
    const texto = generarTextoCompartir();
    try {
      await navigator.clipboard.writeText(texto);
      toast.success('Texto copiado al portapapeles');
    } catch (error) {
      toast.error('Error al copiar');
    }
  };

  // Compartir por correo (texto limpio sin emojis para Outlook)
  const handleCompartirEmail = () => {
    if (!gestion) return;

    const baseUrl = window.location.origin;
    const enlaceCaso = `${baseUrl}/?caso=${gestionId}`;

    let body = '';
    body += `ALERTA 360 - CNC Guatemala\n`;
    body += `=============================\n\n`;
    body += `${gestion.nomenclatura} - ${gestion.tipo_gestion}\n\n`;
    body += `Enlace directo al caso:\n${enlaceCaso}\n\n`;
    body += `--- INFORMACION GENERAL ---\n`;
    body += `Institucion: ${gestion.institucion || 'N/A'}\n`;
    body += `Asesor CNC: ${gestion.nombre_asesor_cnc || 'N/A'}\n`;
    body += `Estado: ${gestion.estado || 'N/A'}\n`;
    body += `Fecha asignacion: ${gestion.fecha_asignacion || 'N/A'}\n`;
    if (gestion.fecha_vencimiento) {
      body += `Fecha vencimiento: ${gestion.fecha_vencimiento}\n`;
    }
    body += `\n`;

    body += `--- DESCRIPCION ---\n`;
    body += `${htmlToPlainText(gestion.descripcion) || 'Sin descripcion'}\n\n`;

    const campos = gestion.campos_especificos || {};
    const labelMap = {
      'network': 'Red Social', 'author': 'Autor', 'tematica': 'Tematica',
      'persons': 'Personas relacionadas', 'link': 'Enlace publicacion',
      'unidad_compradora': 'Unidad compradora', 'enlace_guatecompras': 'Enlace Guatecompras',
      'referencia': 'Referencia', 'numero_oficio': 'Referencia', 'caso_origen': 'Referencia'
    };
    const camposSimples = Object.entries(campos).filter(
      ([key, value]) => value && !Array.isArray(value) && typeof value !== 'object' && key !== 'detalle_nogs'
    );
    if (camposSimples.length > 0) {
      body += `--- INFORMACION ESPECIFICA ---\n`;
      camposSimples.forEach(([key, value]) => {
        const label = labelMap[key] || key.replace(/_/g, ' ');
        body += `${label}: ${value}\n`;
      });
      body += `\n`;
    }

    if (gestion.asesor_probidad_nombre) {
      body += `--- DERIVACION INSTANCIA DE PROBIDAD ---\n`;
      body += `Instancia: ${gestion.asesor_probidad_nombre}\n`;
      if (gestion.fecha_vencimiento_probidad) {
        body += `Vencimiento: ${gestion.fecha_vencimiento_probidad}\n`;
      }
      body += `\n`;
    }

    body += `=============================\n`;
    body += `Sistema ALERTA 360 - CNC Guatemala\n`;

    const subject = encodeURIComponent(`[ALERTA 360] ${gestion.nomenclatura} - ${gestion.tipo_gestion}`);
    const encodedBody = encodeURIComponent(body);
    window.open(`mailto:?subject=${subject}&body=${encodedBody}`, '_blank');
    toast.success('Abriendo cliente de correo...');
    setShowShareModal(false);
  };

  // Compartir por WhatsApp
  const handleCompartirWhatsApp = () => {
    const texto = encodeURIComponent(generarTextoCompartir());
    window.open(`https://wa.me/?text=${texto}`, '_blank');
    toast.success('Abriendo WhatsApp...');
    setShowShareModal(false);
  };

  const handleAgregarAvance = async () => {
    if (!nuevoAvance.trim()) {
      toast.error('Ingrese un avance');
      return;
    }

    try {
      const payload = {
        texto: nuevoAvance,
        nuevo_estado: nuevoEstado || undefined,
        link_onedrive: linkOneDrive.trim() || undefined
      };
      
      await axios.post(`${API}/gestiones/${gestionId}/avances`, payload);
      
      toast.success('Avance registrado');
      setNuevoAvance('');
      setLinkOneDrive('');
      // Mantener el estado seleccionado actual
      fetchGestion();
    } catch (error) {
      console.error('Error al agregar avance:', error);
      toast.error('Error al registrar el avance');
    }
  };

  const handleSubirDocumento = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Solo se permiten archivos PDF');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo no debe exceder 5 MB');
      return;
    }

    const formData = new FormData();
    formData.append('archivo', file);

    setUploading(true);
    try {
      await axios.post(`${API}/gestiones/${gestionId}/documentos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Documento subido correctamente');
      fetchGestion();
    } catch (error) {
      console.error('Error al subir documento:', error);
      toast.error(error.response?.data?.detail || 'Error al subir el documento');
    } finally {
      setUploading(false);
    }
  };

  const handleEliminarDocumento = async (documentoId) => {
    if (!window.confirm('¿Está seguro de eliminar este documento?')) return;

    try {
      await axios.delete(`${API}/gestiones/${gestionId}/documentos/${documentoId}`);
      toast.success('Documento eliminado');
      fetchGestion();
    } catch (error) {
      console.error('Error al eliminar documento:', error);
      toast.error('Error al eliminar el documento');
    }
  };

  const getModuloColor = (tipo) => {
    const colores = {
      // Nuevos nombres
      'Monitoreo': '#2196F3',
      'Adquisiciones públicas': '#F44336',
      'Contratación Personal': '#4CAF50',
      'Dirección Ejecutiva': '#FF9800',
      'Casos previos': '#9C27B0',
      // Nombres legacy
      'Monitoreo': '#2196F3',
      'Adquisiciones públicas': '#F44336',
      'Contratación Personal': '#4CAF50',
      'Dirección Ejecutiva': '#FF9800',
      'Casos previos': '#9C27B0',
      // Compatibilidad con nombres antiguos
      'Monitoreo de Medios': '#2196F3',
      'Banderas Rojas': '#F44336',
      'Requerimientos de Instancias de Probidad': '#4CAF50',
      'Contratación Personal': '#4CAF50',
      'Gestión de Información': '#FF9800',
      'Seguimiento': '#9C27B0'
    };
    return colores[tipo] || '#757575';
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem' }}>
        <div className="loader"></div>
        <p style={{ marginTop: '1rem' }}>Cargando detalle...</p>
      </div>
    );
  }

  if (!gestion) {
    return (
      <div className="empty-state">
        <FileText size={48} style={{ opacity: 0.3 }} />
        <p>Gestión no encontrada</p>
        <button className="btn btn-primary" onClick={onBack}>
          Volver
        </button>
      </div>
    );
  }

  return (
    <div className="detalle-gestion-container">
      {/* Header */}
      <div className="detalle-header">
        <button className="btn-back" onClick={onBack}>
          <ArrowLeft size={20} />
          Volver
        </button>
        
        <div className="detalle-title-section" style={{ 
          overflow: 'hidden',
          flex: 1,
          minWidth: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            {/* Badge URGENTE prominente */}
            {gestion.es_urgente && (
              <div style={{
                background: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)',
                color: 'white',
                padding: '0.35rem 0.75rem',
                borderRadius: '4px',
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.5px',
                animation: 'pulse 2s infinite',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                boxShadow: '0 2px 4px rgba(211, 47, 47, 0.4)'
              }}>
                🔴 URGENTE
              </div>
            )}
            <div className="nomenclatura-badge" style={{ 
              backgroundColor: getModuloColor(gestion.tipo_gestion),
              flexShrink: 0
            }}>
              {gestion.nomenclatura}
            </div>
          </div>
          <div style={{ 
            overflow: 'hidden',
            minWidth: 0
          }}>
            <h1 style={{ 
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>{gestion.tipo_gestion}</h1>
            {(() => {
              const plainDesc = htmlToPlainText(gestion.descripcion);
              return plainDesc ? (
                <p style={{ 
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  margin: 0,
                  maxWidth: '500px'
                }}>{plainDesc.slice(0, 80)}{plainDesc.length > 80 ? '...' : ''}</p>
              ) : null;
            })()}
          </div>
        </div>

        <div className="detalle-header-actions">
          {/* Indicador de tiempo de gestión (Mejora 3) */}
          {(() => {
            const tiempo = calcularTiempoGestion(
              gestion.createdAt, 
              gestion.estado === 'Completada / Archivada' ? gestion.fecha_completado : null
            );
            return tiempo ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: gestion.estado === 'Completada / Archivada' ? '#E8F5E9' : '#FFF3E0',
                borderRadius: '8px',
                border: `1px solid ${gestion.estado === 'Completada / Archivada' ? '#4CAF50' : '#FF9800'}`
              }}>
                <Timer size={18} style={{ color: gestion.estado === 'Completada / Archivada' ? '#4CAF50' : '#FF9800' }} />
                <div style={{ fontSize: '0.85rem' }}>
                  <div style={{ fontWeight: 600, color: gestion.estado === 'Completada / Archivada' ? '#2E7D32' : '#E65100' }}>
                    {tiempo.texto}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#666' }}>
                    {gestion.estado === 'Completada / Archivada' ? 'Tiempo total' : 'Tiempo transcurrido'}
                  </div>
                </div>
              </div>
            ) : null;
          })()}
          
          {/* Indicador / Toggle de Caso Urgente */}
          {puedeRealizarAcciones && gestion.estado !== 'Completada / Archivada' && (
            <button
              className="btn"
              onClick={handleToggleUrgente}
              disabled={guardandoUrgente}
              title={gestion.es_urgente ? 'Desmarcar como urgente' : 'Marcar como urgente'}
              style={{
                background: gestion.es_urgente 
                  ? 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)' 
                  : 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
                color: gestion.es_urgente ? 'white' : '#E65100',
                border: gestion.es_urgente ? 'none' : '1px solid #FF9800',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <AlertTriangle size={16} />
              {gestion.es_urgente ? '🔴 URGENTE' : 'Marcar Urgente'}
            </button>
          )}
          
          {/* Badge de urgente (solo lectura) */}
          {gestion.es_urgente && !puedeRealizarAcciones && (
            <span style={{
              background: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '20px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.85rem'
            }}>
              <AlertTriangle size={16} />
              🔴 URGENTE
            </span>
          )}
          
          <button className="btn btn-secondary" onClick={handleGenerarPDF} title="Generar PDF">
            <Download size={18} />
            PDF
          </button>
          <button className="btn btn-secondary" onClick={() => setShowShareModal(true)} title="Compartir">
            <Share2 size={18} />
            Compartir
          </button>
          <span className={`badge badge-large ${gestion.estado === 'Completada' ? 'badge-success' : 'badge-warning'}`}>
            {gestion.estado}
          </span>
        </div>
      </div>

      {/* Modal de Compartir */}
      {showShareModal && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal-compartir" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <Share2 size={20} />
                Compartir Gestión
              </h3>
              <button className="btn-cerrar" onClick={() => setShowShareModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="gestion-preview">
                <div className="preview-header" style={{ borderLeftColor: getModuloColor(gestion.tipo_gestion) }}>
                  <strong>{gestion.nomenclatura}</strong>
                  <span className="tipo-badge">{gestion.tipo_gestion}</span>
                </div>
                <p className="preview-desc">
                  {(() => {
                    const plainDesc = htmlToPlainText(gestion.descripcion);
                    return plainDesc ? `${plainDesc.slice(0, 150)}${plainDesc.length > 150 ? '...' : ''}` : 'Sin descripción';
                  })()}
                </p>
              </div>
              <div className="opciones-compartir">
                <button className="btn-opcion btn-nativo" onClick={handleCompartirNativo}>
                  <Share2 size={20} />
                  <div>
                    <strong>Compartir Nativo</strong>
                    <span>Usar opciones del sistema</span>
                  </div>
                </button>
                <button className="btn-opcion btn-whatsapp" onClick={handleCompartirWhatsApp}>
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <div>
                    <strong>WhatsApp</strong>
                    <span>Compartir por WhatsApp</span>
                  </div>
                </button>
                <button className="btn-opcion btn-email" onClick={handleCompartirEmail}>
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <path d="M22 6l-10 7L2 6"/>
                  </svg>
                  <div>
                    <strong>Correo Electrónico</strong>
                    <span>Enviar por email</span>
                  </div>
                </button>
                <button className="btn-opcion btn-copiar" onClick={handleCopiarTexto}>
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2"/>
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                  </svg>
                  <div>
                    <strong>Copiar Texto</strong>
                    <span>Copiar al portapapeles</span>
                  </div>
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowShareModal(false)}>
                Cerrar
              </button>
              <button className="btn btn-primary" onClick={handleGenerarPDF}>
                <Download size={16} />
                Descargar PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content Grid */}
      <div className="detalle-content-grid">
        {/* SECCIÓN 1: Información Básica */}
        <div className="detalle-section">
          <h2 className="section-title">
            <Clock size={20} />
            Información del Registro
          </h2>
          <div className="info-grid">
            <div className="info-item">
              <label><Clock size={16} /> Creado por</label>
              <div>{gestion.createdBy}</div>
            </div>
            <div className="info-item">
              <label><Calendar size={16} /> Fecha de registro</label>
              <div>{formatLocalDate(gestion.createdAt)}</div>
            </div>
            <div className="info-item">
              <label><Building2 size={16} /> Institución</label>
              <div>{gestion.institucion}</div>
            </div>
            {gestion.producto_obtenido && (
              <div className="info-item">
                <label><FileText size={16} /> Producto Final</label>
                <div style={{ fontWeight: 600, color: '#4CAF50' }}>{gestion.producto_obtenido}</div>
              </div>
            )}
          </div>
        </div>

        {/* SECCIÓN 2: Descripción / Hallazgo */}
        <div className="detalle-section">
          <h2 className="section-title">
            <FileText size={20} />
            Descripción / Hallazgo
          </h2>
          <div style={{ 
            background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
            borderRadius: '8px',
            padding: '1.5rem',
            border: '1px solid #2196F3',
            minHeight: '100px'
          }}>
            {gestion.descripcion ? (
              <RichTextDisplay 
                content={gestion.descripcion} 
                className="descripcion-content"
              />
            ) : (
              <span style={{ color: '#666', fontStyle: 'italic' }}>Sin descripción</span>
            )}
          </div>
        </div>

        {/* SECCIÓN 3: Información Específica (Campos del módulo + Enlaces) */}
        <div className="detalle-section">
          <h2 className="section-title">
            <FileText size={20} />
            Información Específica
          </h2>
          
          {/* Campos específicos del módulo */}
          {gestion.campos_especificos && Object.keys(gestion.campos_especificos).length > 0 && (
            <div className="info-grid" style={{ marginBottom: '1rem' }}>
              {Object.entries(gestion.campos_especificos)
                .filter(([key, value]) => !Array.isArray(value) && typeof value !== 'object')
                .map(([key, value]) => {
                  const labelMap = {
                    'network': 'Red Social',
                    'author': 'Autor o cuenta',
                    'tematica': 'Temática relacionada',
                    'persons': 'Personas relacionadas',
                    'link': 'Enlace publicación',
                    'color_bandera': 'Color de bandera',
                    'indicador_id': 'Indicador',
                    'fecha_inicio_analisis': 'Fecha inicio análisis',
                    'fecha_fin_analisis': 'Fecha fin análisis',
                    'unidad_compradora': 'Unidad compradora',
                    'cantidad_nog': 'Cantidad de NOG',
                    'cantidad_npg': 'Cantidad de NPG',
                    'enlace_guatecompras': 'Enlace Guatecompras',
                    'referencia': 'Referencia',
                    'fecha_solicitud': 'Fecha solicitud',
                    'numero_oficio': 'Referencia',
                    'fecha_oficio': 'Fecha solicitud',
                    'fecha_recepcion': 'Fecha recepción',
                    'canal_recepcion': 'Canal de recepción',
                    'caso_origen': 'Referencia'
                  };
                  const label = labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  
                  // Detectar si el valor es una URL para mostrarlo como enlace
                  const isUrl = typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'));
                  
                  // Buscar nombre del indicador si es indicador_id
                  let displayValue = value;
                  if (key === 'indicador_id' && value && indicadores.length > 0) {
                    const indicador = indicadores.find(ind => ind.id === value);
                    if (indicador) {
                      displayValue = `${indicador.codigo} - ${indicador.nombre_categoria}`;
                    }
                  }
                  
                  return (
                    <div key={key} className="info-item">
                      <label>{label}</label>
                      <div>
                        {isUrl ? (
                          <a href={value} target="_blank" rel="noopener noreferrer" title={value}>
                            {value.length > 50 ? value.substring(0, 50) + '...' : value}
                          </a>
                        ) : (
                          typeof displayValue === 'number' ? displayValue.toLocaleString('es-GT') : displayValue || 'N/A'
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
          
          {/* Detalle de NOGs (si existe) */}
          {gestion.campos_especificos?.detalle_nogs && gestion.campos_especificos.detalle_nogs.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>
                Detalle de NOGs ({gestion.campos_especificos.detalle_nogs.length})
              </h3>
              <div className="table-wrapper">
                <table className="detail-table">
                  <thead>
                    <tr>
                      <th>NOG</th>
                      <th>Descripción</th>
                      <th>Tipo Concurso</th>
                      <th>Estado</th>
                      <th>Fecha Adjudicación</th>
                      <th>Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gestion.campos_especificos.detalle_nogs.map((nog, index) => (
                      <tr key={index}>
                        <td><strong>{nog.nog || 'N/A'}</strong></td>
                        <td>{nog.descripcion || nog.unidad_compradora || 'N/A'}</td>
                        <td>{nog.tipo_concurso || 'N/A'}</td>
                        <td>{nog.status_concurso || 'N/A'}</td>
                        <td>{nog.fecha_adjudicacion ? new Date(nog.fecha_adjudicacion).toLocaleDateString('es-ES') : 'N/A'}</td>
                        <td>{nog.monto ? `Q ${parseFloat(nog.monto).toLocaleString('es-GT', { minimumFractionDigits: 2 })}` : 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Detalle de NPGs (si existe) */}
          {gestion.campos_especificos?.detalle_npgs && gestion.campos_especificos.detalle_npgs.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#1565C0' }}>
                Detalle de NPGs / Concursos ({gestion.campos_especificos.detalle_npgs.length})
              </h3>
              <div className="table-wrapper">
                <table className="detail-table">
                  <thead>
                    <tr>
                      <th>NPG</th>
                      <th>Descripción</th>
                      <th>Tipo Concurso</th>
                      <th>Estado</th>
                      <th>Fecha Adjudicación</th>
                      <th>Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gestion.campos_especificos.detalle_npgs.map((npg, index) => (
                      <tr key={index}>
                        <td><strong>{npg.npg || 'N/A'}</strong></td>
                        <td>{npg.descripcion || 'N/A'}</td>
                        <td>{npg.tipo_concurso || 'N/A'}</td>
                        <td>{npg.status_concurso || 'N/A'}</td>
                        <td>{npg.fecha_adjudicacion ? new Date(npg.fecha_adjudicacion).toLocaleDateString('es-ES') : 'N/A'}</td>
                        <td>{npg.monto ? `Q ${parseFloat(npg.monto).toLocaleString('es-GT', { minimumFractionDigits: 2 })}` : 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Detalle de Proveedores (si existe) */}
          {gestion.campos_especificos?.proveedores && gestion.campos_especificos.proveedores.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#4CAF50' }}>
                Proveedores ({gestion.campos_especificos.proveedores.length})
              </h3>
              <div className="table-wrapper">
                <table className="detail-table">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>NIT</th>
                      <th>Oferta (Q)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gestion.campos_especificos.proveedores.map((prov, index) => (
                      <tr key={index}>
                        <td><strong>{prov.nombre || 'N/A'}</strong></td>
                        <td>{prov.nit || 'N/A'}</td>
                        <td>{prov.oferta ? `Q ${parseFloat(prov.oferta).toLocaleString('es-GT', { minimumFractionDigits: 2 })}` : 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Enlaces Guatecompras (si existe) */}
          {gestion.campos_especificos?.enlaces_guatecompras && gestion.campos_especificos.enlaces_guatecompras.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontWeight: 600 }}>
                <LinkIcon size={16} />
                Enlaces Guatecompras
              </label>
              {gestion.campos_especificos.enlaces_guatecompras.map((link, index) => (
                <a 
                  key={index} 
                  href={link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="reference-link"
                  style={{ display: 'block', marginBottom: '0.25rem' }}
                >
                  {link}
                </a>
              ))}
            </div>
          )}

          {/* Enlaces Referenciales */}
          {gestion.links_referenciales && gestion.links_referenciales.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontWeight: 600 }}>
                <LinkIcon size={16} />
                Enlaces Referenciales
              </label>
              {gestion.links_referenciales.map((link, index) => (
                <a 
                  key={index} 
                  href={link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="reference-link"
                  style={{ display: 'block', marginBottom: '0.25rem' }}
                >
                  {link}
                </a>
              ))}
            </div>
          )}
        </div>

        {/* SECCIÓN 4: Análisis Complementario (Asesor CNC) - Con Historial */}
        <div className="detalle-section">
          <h2 className="section-title">
            <Edit size={20} />
            Análisis Complementario CNC ({historialAnalisis.length})
          </h2>
          
          {/* Historial de análisis existentes */}
          {historialAnalisis.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              {historialAnalisis.map((entrada, index) => (
                <div 
                  key={index}
                  style={{ 
                    background: index === historialAnalisis.length - 1 ? '#FFFDE7' : '#F5F5F5',
                    borderRadius: '8px',
                    padding: '1rem',
                    border: index === historialAnalisis.length - 1 ? '1px solid #FBC02D' : '1px solid #E0E0E0',
                    marginBottom: '0.75rem'
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    marginBottom: '0.5rem'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: '#666' }}>
                      <strong>#{index + 1}</strong> • {entrada.usuario} • {new Date(entrada.fecha).toLocaleString('es-ES')}
                      {entrada.editado_por && (
                        <span style={{ color: '#FF9800', marginLeft: '0.5rem' }}>
                          (Editado por {entrada.editado_por})
                        </span>
                      )}
                    </div>
                    {puedeAnalisisComp && gestion.estado !== 'Completada / Archivada' && editandoAnalisisIndex !== index && (
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button
                          className="btn-icon"
                          onClick={() => handleIniciarEdicionAnalisis(index)}
                          title="Editar análisis"
                          style={{ padding: '0.25rem', color: '#666' }}
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => handleEliminarAnalisis(index)}
                          title="Eliminar análisis"
                          style={{ padding: '0.25rem', color: '#D32F2F' }}
                          disabled={guardandoAnalisis}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {editandoAnalisisIndex === index ? (
                    <div>
                      <RichTextEditor
                        value={textoEdicionAnalisis}
                        onChange={(value) => setTextoEdicionAnalisis(value)}
                        placeholder="Editar análisis complementario..."
                        minHeight="120px"
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={handleCancelarEdicionAnalisis}
                        >
                          <X size={14} />
                          Cancelar
                        </button>
                        <button 
                          className="btn btn-primary btn-sm"
                          onClick={handleGuardarEdicionAnalisis}
                          disabled={guardandoAnalisis}
                        >
                          <Save size={14} />
                          {guardandoAnalisis ? 'Guardando...' : 'Guardar'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.9rem' }}>
                      <RichTextDisplay content={entrada.texto} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Formulario para agregar nuevo análisis - Solo Analista y Admin */}
          {gestion.estado !== 'Completada / Archivada' && puedeAnalisisComp && (
            <div style={{ 
              background: '#E8F5E9',
              borderRadius: '8px',
              padding: '1rem',
              border: '1px solid #4CAF50'
            }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#2E7D32' }}>
                <Plus size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.25rem' }} />
                Agregar nuevo análisis
              </div>
              <RichTextEditor
                value={analisisComplementario}
                onChange={(value) => setAnalisisComplementario(value)}
                placeholder="Escriba un nuevo análisis complementario con formato enriquecido. Soporte para negritas, listas, tablas..."
                minHeight="150px"
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
                <button 
                  className="btn btn-primary"
                  onClick={handleGuardarAnalisis}
                  disabled={guardandoAnalisis || !analisisComplementario.replace(/<[^>]*>/g, '').trim()}
                  style={{ fontSize: '0.875rem', background: '#4CAF50', borderColor: '#4CAF50' }}
                >
                  <Plus size={16} />
                  {guardandoAnalisis ? 'Agregando...' : 'Agregar Análisis'}
                </button>
              </div>
            </div>
          )}
          
          {/* Vista para gestiones archivadas o usuarios sin permisos */}
          {(gestion.estado === 'Completada / Archivada' || !puedeAnalisisComp) && historialAnalisis.length === 0 && (
            <div style={{ 
              background: '#FFFDE7',
              borderRadius: '8px',
              padding: '1rem',
              border: '1px solid #FBC02D',
              color: '#666',
              fontStyle: 'italic'
            }}>
              No se registró análisis complementario
            </div>
          )}
        </div>

        {/* SECCIÓN 5: Asignación / Análisis */}
        <div className="detalle-section">
          <h2 className="section-title">
            <User size={20} />
            Asignación / Análisis
          </h2>
          <div style={{ 
            background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
            borderRadius: '8px',
            padding: '1rem',
            border: '1px solid #2196F3'
          }}>
            <div className="info-grid">
              <div className="info-item">
                <label><User size={16} /> Asesor</label>
                <div style={{ fontWeight: 600 }}>{gestion.nombre_asesor_cnc}</div>
              </div>
              <div className="info-item">
                <label><Calendar size={16} /> Fecha asignación</label>
                <div>{formatLocalDate(gestion.fecha_asignacion)}</div>
              </div>
              <div className="info-item">
                <label><Calendar size={16} /> Fecha vencimiento</label>
                {(() => {
                  const fechaVenc = gestion.fecha_vencimiento ? parseLocalDate(gestion.fecha_vencimiento) : null;
                  const hoy = new Date();
                  hoy.setHours(0, 0, 0, 0);
                  const estaVencida = fechaVenc && fechaVenc < hoy;
                  const yaDerivadaAProbidad = gestion.asesor_probidad_id || gestion.asesor_probidad_nombre;
                  const estaCompletada = gestion.estado === 'Completada / Archivada';
                  
                  // Si ya fue derivada a Probidad, se considera "atendida en tiempo" para CNC
                  const mostrarComoVencida = estaVencida && !yaDerivadaAProbidad && !estaCompletada;
                  const mostrarComoAtendida = estaVencida && yaDerivadaAProbidad && !estaCompletada;
                  
                  return (
                    <div style={{ 
                      color: mostrarComoVencida ? '#D32F2F' : mostrarComoAtendida ? '#2E7D32' : 'inherit',
                      fontWeight: (mostrarComoVencida || mostrarComoAtendida) ? 600 : 'normal'
                    }}>
                      {formatLocalDate(gestion.fecha_vencimiento) || 'Sin fecha límite'}
                      {mostrarComoVencida && ' (VENCIDO)'}
                      {mostrarComoAtendida && ' (Atendido en tiempo - Derivado a Probidad)'}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* SECCIÓN 6: Derivación Instancia Probidad */}
        <div className="detalle-section">
          <h2 className="section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Building2 size={20} />
              Derivación Instancia Probidad
              {!gestion.asesor_probidad_nombre && <span style={{ fontSize: '0.8rem', fontWeight: 'normal', marginLeft: '0.5rem', color: '#E65100' }}>(Pendiente)</span>}
            </span>
            {/* Botón editar solo para Admin cuando ya está derivado */}
            {isAdmin && gestion.asesor_probidad_nombre && gestion.estado !== 'Completada / Archivada' && !editandoProbidad && (
              <button
                onClick={handleEditarProbidad}
                className="btn-secondary"
                style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}
              >
                <Edit size={14} style={{ marginRight: '4px' }} />
                Editar
              </button>
            )}
          </h2>
          <div style={{ 
            background: gestion.asesor_probidad_nombre 
              ? 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)'
              : 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
            borderRadius: '8px',
            padding: '1rem',
            border: `1px solid ${gestion.asesor_probidad_nombre ? '#4CAF50' : '#FF9800'}`
          }}>
            {/* Modo edición para Admin */}
            {editandoProbidad ? (
              <div className="form-panel" style={{ background: 'white', padding: '1rem', borderRadius: '8px' }}>
                <h4 style={{ marginBottom: '1rem', color: '#333' }}>Editar Información de Probidad</h4>
                <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                  <div className="form-field">
                    <label>Instancia de Probidad *</label>
                    <select
                      value={editAsesorProbidadId}
                      onChange={(e) => setEditAsesorProbidadId(e.target.value)}
                    >
                      <option value="">Seleccionar...</option>
                      {asesoresProbidad.map(a => (
                        <option key={a.id} value={a.id}>{a.nombre_completo}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Fecha de Derivación *</label>
                    <input
                      type="date"
                      value={editFechaDerivacion}
                      onChange={(e) => setEditFechaDerivacion(e.target.value)}
                    />
                  </div>
                  <div className="form-field">
                    <label>Fecha de Vencimiento *</label>
                    <input
                      type="date"
                      value={editFechaVencProbidad}
                      onChange={(e) => setEditFechaVencProbidad(e.target.value)}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    className="btn-secondary"
                    onClick={() => setEditandoProbidad(false)}
                    disabled={guardandoEditProbidad}
                  >
                    Cancelar
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleGuardarEditProbidad}
                    disabled={guardandoEditProbidad}
                  >
                    {guardandoEditProbidad ? 'Guardando...' : 'Guardar Cambios'}
                  </button>
                </div>
              </div>
            ) : gestion.asesor_probidad_nombre ? (
              <>
                <div className="info-grid">
                  <div className="info-item">
                    <label><User size={16} /> Instancia de Probidad</label>
                    <div style={{ fontWeight: 600 }}>{gestion.asesor_probidad_nombre}</div>
                  </div>
                  
                  {/* Datos de contacto del asesor */}
                  {asesorProbidadInfo && (
                    <>
                      {asesorProbidadInfo.cargo && (
                        <div className="info-item">
                          <label><Building2 size={16} /> Cargo</label>
                          <div>{asesorProbidadInfo.cargo}</div>
                        </div>
                      )}
                      {asesorProbidadInfo.email && (
                        <div className="info-item">
                          <label><Mail size={16} /> Correo Electrónico</label>
                          <div>
                            <a href={`mailto:${asesorProbidadInfo.email}`} style={{ color: '#1976D2', textDecoration: 'none' }}>
                              {asesorProbidadInfo.email}
                            </a>
                          </div>
                        </div>
                      )}
                      {asesorProbidadInfo.telefono && (
                        <div className="info-item">
                          <label><Phone size={16} /> Teléfono</label>
                          <div>
                            <a href={`tel:${asesorProbidadInfo.telefono}`} style={{ color: '#1976D2', textDecoration: 'none' }}>
                              {asesorProbidadInfo.telefono}
                            </a>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  
                  <div className="info-item">
                    <label><Calendar size={16} /> Fecha Derivación</label>
                    <div>{formatLocalDate(gestion.fecha_asignacion_probidad || gestion.fecha_derivacion) || 'N/A'}</div>
                  </div>
                  <div className="info-item">
                    <label><Calendar size={16} /> Fecha Vencimiento</label>
                    {(() => {
                      const fechaVencProb = gestion.fecha_vencimiento_probidad ? parseLocalDate(gestion.fecha_vencimiento_probidad) : null;
                      const hoy = new Date();
                      hoy.setHours(0, 0, 0, 0);
                      const estaVencida = fechaVencProb && fechaVencProb < hoy && gestion.estado !== 'Completada / Archivada';
                      return (
                        <div style={{ 
                          color: estaVencida ? '#D32F2F' : 'inherit',
                          fontWeight: estaVencida ? 600 : 'normal'
                        }}>
                          {formatLocalDate(gestion.fecha_vencimiento_probidad) || 'Sin fecha límite'}
                          {estaVencida && ' (VENCIDO)'}
                        </div>
                      );
                    })()}
                  </div>
                  
                  {/* Prórroga de plazo */}
                  {gestion.prorroga_probidad > 0 && (
                    <div className="info-item">
                      <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}><Clock size={16} /> Prórroga Otorgada</span>
                        {gestion.estado !== 'Completada / Archivada' && puedeOtorgarProrroga && (
                          <button
                            data-testid="btn-editar-prorroga"
                            onClick={abrirEditarProrroga}
                            style={{
                              background: 'none', border: '1px solid #90CAF9', borderRadius: '4px',
                              color: '#1565C0', cursor: 'pointer', padding: '2px 8px', fontSize: '0.72rem',
                              fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px'
                            }}
                          >
                            <Pencil size={11} /> Editar
                          </button>
                        )}
                      </label>
                      <div style={{ color: '#2196F3', fontWeight: 500 }}>
                        {gestion.prorroga_probidad} días adicionales
                        {gestion.prorroga_probidad_motivo && (
                          <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                            Motivo: {gestion.prorroga_probidad_motivo}
                          </div>
                        )}
                        {gestion.fecha_vencimiento_probidad_original && (
                          <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.25rem' }}>
                            Fecha original: {formatLocalDate(gestion.fecha_vencimiento_probidad_original)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Comentario de derivación */}
                {gestion.bitacora && gestion.bitacora.length > 0 && (() => {
                  const entradaDerivacion = gestion.bitacora.find(b => b.accion === 'DERIVADO_A_PROBIDAD');
                  if (entradaDerivacion && entradaDerivacion.detalles) {
                    return (
                      <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(255,255,255,0.7)', borderRadius: '6px' }}>
                        <label style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                          Comentario de Derivación:
                        </label>
                        <div style={{ color: '#333', lineHeight: 1.5 }}>{entradaDerivacion.detalles}</div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Calificación de Instancia de Probidad */}
                {gestion.estado === 'Completada / Archivada' && gestion.calificacion_instancia && (
                  <div style={{ 
                    marginTop: '1rem',
                    padding: '0.75rem',
                    background: 'linear-gradient(135deg, #F3E5F5 0%, #E1BEE7 100%)',
                    borderRadius: '6px',
                    border: '1px solid #9C27B0'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <label style={{ fontWeight: 600, color: '#7B1FA2', fontSize: '0.875rem' }}>
                          ⭐ Calificación de Instancia de Probidad
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
                          <span style={{ fontSize: '1.25rem', color: '#FFB800' }}>
                            {'★'.repeat(gestion.calificacion_instancia.puntuacion)}
                            {'☆'.repeat(5 - gestion.calificacion_instancia.puntuacion)}
                          </span>
                          <span style={{ fontWeight: 600 }}>({gestion.calificacion_instancia.puntuacion}/5)</span>
                        </div>
                      </div>
                      <button 
                        className="btn btn-secondary"
                        onClick={() => setShowVerCalificacionModal(true)}
                        style={{ fontSize: '0.75rem' }}
                      >
                        Ver Detalles
                      </button>
                    </div>
                  </div>
                )}

                {/* Botón para otorgar prórroga - Solo Analista y Admin */}
                {gestion.estado !== 'Completada / Archivada' && gestion.fecha_vencimiento_probidad && puedeOtorgarProrroga && (
                  <div style={{ marginTop: '1rem', textAlign: 'right' }}>
                    <button 
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowProrrogaModal(true)}
                      style={{ fontSize: '0.8rem' }}
                    >
                      <Clock size={14} /> Otorgar Prórroga
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div style={{ color: '#E65100', fontStyle: 'italic' }}>
                Este caso aún no ha sido derivado a una instancia de probidad. El Asesor CNC debe completar el análisis y proceder con la derivación si corresponde.
              </div>
            )}
          </div>

          {/* Panel de Acciones de Flujo */}
          {(gestion.estado === 'Registrado' || gestion.estado === 'En proceso de análisis') && !gestion.asesor_probidad_nombre && puedeRealizarAcciones && (
            <div style={{ 
              background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)', 
              border: '1px solid #FF9800',
              borderRadius: '8px',
              padding: '1rem',
              marginTop: '1rem'
            }}>
              <h4 style={{ margin: '0 0 0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#E65100' }}>
                <AlertTriangle size={18} />
                Acciones de Evaluación (Asesor CNC)
              </h4>
              <p style={{ fontSize: '0.875rem', color: '#5D4037', marginBottom: '1rem' }}>
                Como Asesor CNC, evalúe si esta gestión es procedente para derivar a la Instancia de Probidad.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button 
                  className="btn btn-success"
                  onClick={() => {
                    handleMarcarProcedente(true);
                    fetchAsesoresProbidad();
                    setShowDerivarModal(true);
                  }}
                >
                  <Send size={16} />
                  Derivar a Instancia de Probidad
                </button>
                {puedeMarcarImprocedente && (
                  <button 
                    className="btn"
                    style={{ 
                      background: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)',
                      color: 'white',
                      border: 'none'
                    }}
                    onClick={() => setShowImprocedenteModal(true)}
                  >
                    <X size={16} />
                    Marcar Improcedente
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Botón para completar gestión - Solo Analista y Admin */}
          {gestion.estado !== 'Completada / Archivada' && gestion.asesor_probidad_nombre && puedeCalificarInstancia && (
            <div style={{ 
              background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)', 
              border: '1px solid #4CAF50',
              borderRadius: '8px',
              padding: '1rem',
              marginTop: '1rem',
              textAlign: 'center'
            }}>
              <p style={{ fontSize: '0.875rem', color: '#2E7D32', marginBottom: '1rem' }}>
                Esta gestión ya fue derivada a la Instancia de Probidad. Una vez completado el análisis, puede cerrar la gestión.
              </p>
              <button 
                className="btn btn-success"
                onClick={() => setShowCalificarModal(true)}
              >
                <CheckCircle size={16} />
                Completar / Archivar Gestión
              </button>
            </div>
          )}
        </div>

        {/* Modal de Derivación a Probidad */}
        {showDerivarModal && (
          <div className="modal-overlay" onClick={() => setShowDerivarModal(false)}>
            <div className="modal-compartir" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header">
                <h3><Send size={20} /> Derivar a Instancia de Probidad</h3>
                <button className="btn-cerrar" onClick={() => setShowDerivarModal(false)}><X size={20} /></button>
              </div>
              <div className="modal-body">
                <p style={{ marginBottom: '1rem', color: '#666' }}>
                  Seleccione el asesor de probidad de la institución <strong>{gestion.institucion}</strong>
                </p>
                
                {asesoresProbidad.length === 0 ? (
                  <div style={{ background: '#FFF3E0', padding: '1rem', borderRadius: '6px', marginBottom: '1rem' }}>
                    <p style={{ margin: 0, color: '#E65100' }}>
                      ⚠️ No hay asesores de probidad registrados para esta institución. 
                      Solicite al administrador que registre asesores en el catálogo.
                    </p>
                  </div>
                ) : (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                      Asesor de Probidad *
                    </label>
                    <select
                      value={selectedAsesorProbidad}
                      onChange={(e) => setSelectedAsesorProbidad(e.target.value)}
                      style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
                    >
                      <option value="">Seleccione un asesor</option>
                      {asesoresProbidad.map(asesor => (
                        <option key={asesor.id} value={asesor.id}>
                          {asesor.nombre_completo} - {asesor.cargo || 'Sin cargo'}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Fechas de asignación y vencimiento para Probidad */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                      Fecha Asignación Probidad *
                    </label>
                    <input
                      type="date"
                      value={fechaAsignacionProbidad}
                      onChange={(e) => setFechaAsignacionProbidad(e.target.value)}
                      style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                      Fecha Vencimiento Probidad
                    </label>
                    <input
                      type="date"
                      value={fechaVencimientoProbidad}
                      onChange={(e) => setFechaVencimientoProbidad(e.target.value)}
                      style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
                    />
                    <small style={{ color: '#666', fontSize: '0.75rem' }}>Fecha límite recomendada para la instancia</small>
                  </div>
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Comentario de derivación
                  </label>
                  <textarea
                    value={comentarioDerivacion}
                    onChange={(e) => setComentarioDerivacion(e.target.value)}
                    placeholder="Agregar notas para el asesor de probidad..."
                    rows={3}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>
              </div>
              <div className="modal-footer" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', padding: '1rem', borderTop: '1px solid #eee' }}>
                <button className="btn btn-secondary" onClick={() => setShowDerivarModal(false)}>Cancelar</button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleDerivarProbidad}
                  disabled={!selectedAsesorProbidad || asesoresProbidad.length === 0}
                >
                  <Send size={16} />
                  Derivar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Calificación al Completar/Archivar */}
        {/* Renderizado directo sin wrapper de modal para evitar errores de removeChild */}
        {showCalificarModal && (
          <div 
            style={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}
            onClick={() => setShowCalificarModal(false)}
          >
            <div 
              onClick={(e) => e.stopPropagation()} 
              style={{ 
                maxWidth: '620px', 
                width: '95%',
                background: 'white', 
                borderRadius: '12px', 
                boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                maxHeight: '90vh',
                overflow: 'auto'
              }}
            >
              <div style={{ 
                background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)', 
                color: 'white', 
                padding: '1rem 1.5rem',
                borderRadius: '12px 12px 0 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <CheckCircle size={20} /> Completar Gestión
                </h3>
                <button 
                  onClick={() => setShowCalificarModal(false)} 
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: 'white', 
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              <div style={{ padding: '1.5rem' }}>
                {/* Producto Final */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Producto Final Obtenido *
                  </label>
                  <select
                    value={productoFinal}
                    onChange={(e) => setProductoFinal(e.target.value)}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  >
                    <option value="">Seleccione el producto</option>
                    {PRODUCTOS_FINALES.map(prod => (
                      <option key={prod} value={prod}>{prod}</option>
                    ))}
                  </select>
                </div>

                {/* Evaluación de Instancia de Probidad con Checklist (solo si tiene asesor asignado) */}
                {gestion && gestion.asesor_probidad_nombre && (
                  <div style={{ 
                    background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
                    border: '1px solid #2196F3',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem'
                  }} data-testid="checklist-probidad-section">
                    <h4 style={{ margin: '0 0 0.5rem', color: '#1565C0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <UserCheck size={18} />
                      Evaluación de Instancia de Probidad
                    </h4>
                    <p style={{ fontSize: '0.85rem', color: '#1976D2', marginBottom: '0.75rem' }}>
                      Evalúe el desempeño de <strong>{gestion.asesor_probidad_nombre}</strong> ({gestion.institucion}) respondiendo las siguientes preguntas:
                    </p>

                    {/* Barra de progreso del checklist */}
                    {(() => {
                      const respondidas = Object.values(checklistProbidad).filter(v => v !== null).length;
                      const pct = (respondidas / 5) * 100;
                      return (
                        <div style={{ marginBottom: '0.75rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#1565C0', marginBottom: '0.25rem' }}>
                            <span>Progreso del checklist</span>
                            <span>{respondidas}/5 respondidas</span>
                          </div>
                          <div style={{ height: '6px', background: '#BBDEFB', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#4CAF50' : '#1976D2', transition: 'width 0.3s ease', borderRadius: '3px' }} />
                          </div>
                        </div>
                      );
                    })()}
                    
                    {/* Checklist de 5 preguntas */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                      {[
                        { key: 'dentro_plazo', label: '1. ¿Se atendió dentro del plazo establecido?' },
                        { key: 'averiguacion_caso', label: '2. ¿Se realizó una averiguación adecuada del caso?' },
                        { key: 'respuesta_tecnica', label: '3. ¿Se proporcionó una respuesta técnica fundamentada?' },
                        { key: 'producto_coherente', label: '4. ¿El producto entregado es coherente con lo solicitado?' },
                        { key: 'actuacion_objetiva', label: '5. ¿La instancia actuó de forma objetiva e imparcial?' }
                      ].map((pregunta) => (
                        <div 
                          key={pregunta.key}
                          style={{ 
                            background: checklistProbidad[pregunta.key] === null ? 'white' : checklistProbidad[pregunta.key] ? '#E8F5E9' : '#FFF3E0',
                            border: `1px solid ${checklistProbidad[pregunta.key] === null ? '#BBDEFB' : checklistProbidad[pregunta.key] ? '#A5D6A7' : '#FFB74D'}`,
                            borderRadius: '6px',
                            padding: '0.6rem 0.75rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'all 0.2s ease'
                          }}
                          data-testid={`checklist-${pregunta.key}`}
                        >
                          <span style={{ fontSize: '0.85rem', fontWeight: 500, flex: 1 }}>{pregunta.label}</span>
                          <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                            <button
                              type="button"
                              onClick={() => setChecklistProbidad(prev => ({ ...prev, [pregunta.key]: true }))}
                              style={{
                                padding: '0.3rem 0.75rem',
                                borderRadius: '4px',
                                border: checklistProbidad[pregunta.key] === true ? '2px solid #2E7D32' : '1px solid #C8E6C9',
                                background: checklistProbidad[pregunta.key] === true ? '#4CAF50' : '#F1F8E9',
                                color: checklistProbidad[pregunta.key] === true ? 'white' : '#2E7D32',
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                transition: 'all 0.15s ease'
                              }}
                              data-testid={`checklist-${pregunta.key}-si`}
                            >
                              Sí
                            </button>
                            <button
                              type="button"
                              onClick={() => setChecklistProbidad(prev => ({ ...prev, [pregunta.key]: false }))}
                              style={{
                                padding: '0.3rem 0.75rem',
                                borderRadius: '4px',
                                border: checklistProbidad[pregunta.key] === false ? '2px solid #E65100' : '1px solid #FFE0B2',
                                background: checklistProbidad[pregunta.key] === false ? '#FF9800' : '#FFF8E1',
                                color: checklistProbidad[pregunta.key] === false ? 'white' : '#E65100',
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                transition: 'all 0.15s ease'
                              }}
                              data-testid={`checklist-${pregunta.key}-no`}
                            >
                              No
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Calificación auto-calculada con opción de override */}
                    <div style={{ 
                      background: 'white', 
                      border: '1px solid #90CAF9', 
                      borderRadius: '8px', 
                      padding: '0.75rem',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1565C0' }}>Calificación resultante</span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', color: '#666', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={overrideCalificacion}
                            onChange={(e) => setOverrideCalificacion(e.target.checked)}
                            data-testid="override-calificacion-checkbox"
                          />
                          Ajustar manualmente
                        </label>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div 
                          style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}
                          onMouseLeave={() => setHoverCalificacion(0)}
                        >
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => { if (overrideCalificacion) { setCalificacion(star); } }}
                              onMouseEnter={() => { if (overrideCalificacion) setHoverCalificacion(star); }}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: overrideCalificacion ? 'pointer' : 'default',
                                fontSize: '32px',
                                color: star <= (overrideCalificacion ? (hoverCalificacion || calificacion) : calificacion) ? '#FFB800' : '#E0E0E0',
                                padding: '4px',
                                transition: 'transform 0.15s ease, color 0.15s ease',
                                outline: 'none',
                                transform: overrideCalificacion && star <= hoverCalificacion ? 'scale(1.15)' : 'scale(1)',
                                opacity: overrideCalificacion ? 1 : 0.85
                              }}
                              data-testid={`star-rating-${star}`}
                            >
                              ★
                            </button>
                          ))}
                        </div>
                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', fontWeight: 600, color: 
                          calificacion <= 1 ? '#C62828' :
                          calificacion === 2 ? '#E65100' :
                          calificacion === 3 ? '#F57F17' :
                          calificacion === 4 ? '#2E7D32' :
                          calificacion === 5 ? '#1565C0' : '#666'
                        }}>
                          {calificacion === 0 && 'Complete el checklist para obtener calificación'}
                          {calificacion === 1 && '1/5 - Muy Deficiente'}
                          {calificacion === 2 && '2/5 - Deficiente'}
                          {calificacion === 3 && '3/5 - Regular'}
                          {calificacion === 4 && '4/5 - Bueno'}
                          {calificacion === 5 && '5/5 - Excelente'}
                        </p>
                        {!overrideCalificacion && calificacion > 0 && (
                          <p style={{ margin: '0.25rem 0 0', fontSize: '0.7rem', color: '#999' }}>
                            Calculada automáticamente. Active "Ajustar manualmente" para modificar.
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Observaciones */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem' }}>
                        Observaciones {calificacion > 0 && calificacion <= 3 && <span style={{ color: '#C62828' }}>* (obligatorio para calificación ≤ 3)</span>}
                      </label>
                      <textarea
                        value={observacionCalificacion}
                        onChange={(e) => setObservacionCalificacion(e.target.value)}
                        placeholder={calificacion <= 3 && calificacion > 0 
                          ? "OBLIGATORIO: Detalle las razones de la baja calificación y recomendaciones de mejora..."
                          : "Agregue observaciones sobre el desempeño de la instancia de probidad..."}
                        rows={3}
                        style={{ 
                          width: '100%', 
                          padding: '0.75rem', 
                          borderRadius: '6px', 
                          border: calificacion > 0 && calificacion <= 3 && !observacionCalificacion.trim() ? '2px solid #C62828' : '1px solid #90CAF9', 
                          fontSize: '0.875rem' 
                        }}
                        data-testid="observacion-calificacion"
                      />
                    </div>

                    {/* Texto orientativo */}
                    <div style={{ 
                      marginTop: '0.75rem', 
                      padding: '0.5rem 0.75rem', 
                      background: '#E8EAF6', 
                      borderRadius: '6px', 
                      borderLeft: '3px solid #3F51B5',
                      fontSize: '0.75rem', 
                      color: '#283593' 
                    }}>
                      <strong>Orientación:</strong> La calificación se calcula automáticamente según las respuestas del checklist. 
                      Cada "Sí" equivale a 1 punto (máximo 5). Si necesita ajustar la calificación, active la opción "Ajustar manualmente". 
                      Las observaciones son obligatorias cuando la calificación es igual o menor a 3 estrellas.
                    </div>
                  </div>
                )}

                {gestion && !gestion.asesor_probidad_nombre && (
                  <div style={{ 
                    background: '#FFF3E0', 
                    border: '1px solid #FF9800', 
                    borderRadius: '8px', 
                    padding: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <p style={{ margin: 0, color: '#E65100', fontSize: '0.875rem' }}>
                      ℹ️ Esta gestión no tiene asignado un asesor de probidad, por lo que no se requiere calificación.
                    </p>
                  </div>
                )}
              </div>
              <div style={{ 
                display: 'flex', 
                gap: '0.5rem', 
                justifyContent: 'flex-end', 
                padding: '1rem 1.5rem', 
                borderTop: '1px solid #eee',
                background: '#f9f9f9',
                borderRadius: '0 0 12px 12px'
              }}>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowCalificarModal(false)}
                >
                  Cancelar
                </button>
                <button 
                  className="btn btn-success"
                  onClick={handleCompletarGestion}
                  disabled={!productoFinal || (gestion?.asesor_probidad_nombre && (calificacion === 0 || Object.values(checklistProbidad).some(v => v === null) || (calificacion <= 3 && !observacionCalificacion.trim())))}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <CheckCircle size={16} />
                  Completar Gestión
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Avances */}
        <div className="detalle-section">
          <h2 className="section-title">
            <CheckCircle size={20} />
            Registro de Avances ({gestion.avances?.length || 0})
          </h2>
          
          {/* Formulario de avances - solo Analista y Admin pueden registrar avances */}
          {puedeRegistrarAvance && gestion.estado !== 'Completada / Archivada' ? (
            <div className="avances-form" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Cambio de estado */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem' }}>
                    Cambiar Estado
                  </label>
                  <select
                    value={nuevoEstado}
                    onChange={(e) => setNuevoEstado(e.target.value)}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid var(--color-border)' }}
                  >
                    <option value="">-- Sin cambio de estado --</option>
                    {ESTADOS_GESTION.map((estado) => (
                      <option 
                        key={estado} 
                        value={estado} 
                        disabled={estado === gestion.estado || estado === 'Completada / Archivada'}
                      >
                        {estado} {estado === gestion.estado ? '(actual)' : estado === 'Completada / Archivada' ? '(solo por calificación)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem' }}>
                    <LinkIcon size={14} style={{ marginRight: '0.25rem' }} />
                    Link de OneDrive (Evidencia)
                  </label>
                  <input
                    type="url"
                    value={linkOneDrive}
                    onChange={(e) => setLinkOneDrive(e.target.value)}
                    placeholder="https://onedrive.live.com/..."
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid var(--color-border)' }}
                  />
                </div>
              </div>
              
              {/* Texto del avance */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem' }}>
                  Descripción del Avance
                </label>
                <textarea
                  value={nuevoAvance}
                  onChange={(e) => setNuevoAvance(e.target.value)}
                  placeholder="Escriba un nuevo avance o comentario..."
                  rows={3}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid var(--color-border)' }}
                />
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn btn-primary" onClick={handleAgregarAvance}>
                  <Save size={16} />
                  Registrar Avance
                </button>
              </div>
            </div>
          ) : (
            <div style={{ 
              background: '#f5f5f5', 
              padding: '1rem', 
              borderRadius: '8px', 
              marginBottom: '1rem',
              color: '#666',
              fontStyle: 'italic',
              textAlign: 'center'
            }}>
              Solo usuarios con permisos de edición pueden registrar avances.
            </div>
          )}

          {gestion.avances && gestion.avances.length > 0 ? (
            <div className="avances-list" style={{ marginTop: '1.5rem' }}>
              {gestion.avances.map((avance, index) => (
                <div key={index} className="avance-item">
                  <div className="avance-number">#{gestion.avances.length - index}</div>
                  <div className="avance-text">
                    {typeof avance === 'object' ? (
                      <>
                        <p>{avance.texto}</p>
                        {avance.estado_cambio && (
                          <small style={{ color: '#1976D2', display: 'block', marginTop: '0.25rem' }}>
                            📊 Estado cambiado a: <strong>{avance.estado_cambio}</strong>
                          </small>
                        )}
                        {avance.link_onedrive && (
                          <small style={{ display: 'block', marginTop: '0.25rem' }}>
                            📎 <a href={avance.link_onedrive} target="_blank" rel="noopener noreferrer" style={{ color: '#0078D4' }}>
                              Ver documento en OneDrive
                            </a>
                          </small>
                        )}
                      </>
                    ) : (
                      avance
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '1rem' }}>
              No hay avances registrados
            </p>
          )}
        </div>

        {/* SECCIÓN: Acciones de Seguimiento (Mejora 2 - Solo Analista y Admin) */}
        {gestion.asesor_probidad_nombre && (
          <div className="detalle-section" style={{ background: '#F3E5F5', border: '1px solid #9C27B0' }}>
            <h2 className="section-title" style={{ color: '#7B1FA2' }}>
              <Phone size={20} />
              Acciones de Seguimiento
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '1rem' }}>
              {puedeMarcarSeguimiento 
                ? `Registre las acciones de contacto y gestión realizadas durante el seguimiento del caso.${gestion.estado === 'Completada / Archivada' ? ' (Puede seguir actualizando después del cierre)' : ''}`
                : 'Solo los Analistas pueden registrar acciones de seguimiento.'
              }
            </p>
            
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '1rem',
              marginBottom: '1rem',
              justifyContent: 'center'
            }}>
              {/* Llamadas telefónicas */}
              <div style={{ 
                background: 'white', 
                padding: '1rem', 
                borderRadius: '8px',
                textAlign: 'center',
                border: '1px solid #E1BEE7',
                minWidth: '120px',
                flex: '1 1 120px',
                maxWidth: '160px',
                opacity: puedeMarcarSeguimiento ? 1 : 0.6
              }}>
                <Phone size={24} style={{ color: '#4CAF50', marginBottom: '0.5rem' }} />
                <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.5rem' }}>Llamadas</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={() => setAccionesSeguimiento(prev => ({...prev, llamadas: Math.max(0, prev.llamadas - 1)}))}
                    style={{ padding: '0.25rem 0.5rem', minWidth: '30px' }}
                    disabled={!puedeMarcarSeguimiento}
                  >-</button>
                  <span style={{ fontWeight: 700, fontSize: '1.25rem', minWidth: '30px' }}>{accionesSeguimiento.llamadas}</span>
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => setAccionesSeguimiento(prev => ({...prev, llamadas: prev.llamadas + 1}))}
                    style={{ padding: '0.25rem 0.5rem', minWidth: '30px' }}
                    disabled={!puedeMarcarSeguimiento}
                  >+</button>
                </div>
              </div>
              
              {/* Correos */}
              <div style={{ 
                background: 'white', 
                padding: '1rem', 
                borderRadius: '8px',
                textAlign: 'center',
                border: '1px solid #E1BEE7',
                minWidth: '120px',
                flex: '1 1 120px',
                maxWidth: '160px',
                opacity: puedeMarcarSeguimiento ? 1 : 0.6
              }}>
                <Mail size={24} style={{ color: '#2196F3', marginBottom: '0.5rem' }} />
                <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.5rem' }}>Correos</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={() => setAccionesSeguimiento(prev => ({...prev, correos: Math.max(0, prev.correos - 1)}))}
                    style={{ padding: '0.25rem 0.5rem', minWidth: '30px' }}
                    disabled={!puedeMarcarSeguimiento}
                  >-</button>
                  <span style={{ fontWeight: 700, fontSize: '1.25rem', minWidth: '30px' }}>{accionesSeguimiento.correos}</span>
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => setAccionesSeguimiento(prev => ({...prev, correos: prev.correos + 1}))}
                    style={{ padding: '0.25rem 0.5rem', minWidth: '30px' }}
                    disabled={!puedeMarcarSeguimiento}
                  >+</button>
                </div>
              </div>
              
              {/* WhatsApp */}
              <div style={{ 
                background: 'white', 
                padding: '1rem', 
                borderRadius: '8px',
                textAlign: 'center',
                border: '1px solid #E1BEE7',
                minWidth: '120px',
                flex: '1 1 120px',
                maxWidth: '160px',
                opacity: puedeMarcarSeguimiento ? 1 : 0.6
              }}>
                <MessageCircle size={24} style={{ color: '#25D366', marginBottom: '0.5rem' }} />
                <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.5rem' }}>WhatsApp</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={() => setAccionesSeguimiento(prev => ({...prev, whatsapp: Math.max(0, prev.whatsapp - 1)}))}
                    style={{ padding: '0.25rem 0.5rem', minWidth: '30px' }}
                    disabled={!puedeMarcarSeguimiento}
                  >-</button>
                  <span style={{ fontWeight: 700, fontSize: '1.25rem', minWidth: '30px' }}>{accionesSeguimiento.whatsapp}</span>
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => setAccionesSeguimiento(prev => ({...prev, whatsapp: prev.whatsapp + 1}))}
                    style={{ padding: '0.25rem 0.5rem', minWidth: '30px' }}
                    disabled={!puedeMarcarSeguimiento}
                  >+</button>
                </div>
              </div>
              
              {/* Reuniones/Meets */}
              <div style={{ 
                background: 'white', 
                padding: '1rem', 
                borderRadius: '8px',
                textAlign: 'center',
                border: '1px solid #E1BEE7',
                minWidth: '120px',
                flex: '1 1 120px',
                maxWidth: '160px',
                opacity: puedeMarcarSeguimiento ? 1 : 0.6
              }}>
                <Video size={24} style={{ color: '#FF5722', marginBottom: '0.5rem' }} />
                <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.5rem' }}>Reuniones</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={() => setAccionesSeguimiento(prev => ({...prev, reuniones: Math.max(0, prev.reuniones - 1)}))}
                    style={{ padding: '0.25rem 0.5rem', minWidth: '30px' }}
                    disabled={!puedeMarcarSeguimiento}
                  >-</button>
                  <span style={{ fontWeight: 700, fontSize: '1.25rem', minWidth: '30px' }}>{accionesSeguimiento.reuniones}</span>
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => setAccionesSeguimiento(prev => ({...prev, reuniones: prev.reuniones + 1}))}
                    style={{ padding: '0.25rem 0.5rem', minWidth: '30px' }}
                    disabled={!puedeMarcarSeguimiento}
                  >+</button>
                </div>
              </div>
              
              {/* Visitas presenciales */}
              <div style={{ 
                background: 'white', 
                padding: '1rem', 
                borderRadius: '8px',
                textAlign: 'center',
                border: '1px solid #E1BEE7',
                minWidth: '120px',
                flex: '1 1 120px',
                maxWidth: '160px',
                opacity: puedeMarcarSeguimiento ? 1 : 0.6
              }}>
                <MapPin size={24} style={{ color: '#9C27B0', marginBottom: '0.5rem' }} />
                <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.5rem' }}>Visitas</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={() => setAccionesSeguimiento(prev => ({...prev, visitas: Math.max(0, prev.visitas - 1)}))}
                    style={{ padding: '0.25rem 0.5rem', minWidth: '30px' }}
                    disabled={!puedeMarcarSeguimiento}
                  >-</button>
                  <span style={{ fontWeight: 700, fontSize: '1.25rem', minWidth: '30px' }}>{accionesSeguimiento.visitas}</span>
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => setAccionesSeguimiento(prev => ({...prev, visitas: prev.visitas + 1}))}
                    style={{ padding: '0.25rem 0.5rem', minWidth: '30px' }}
                    disabled={!puedeMarcarSeguimiento}
                  >+</button>
                </div>
              </div>
            </div>
            
            {puedeMarcarSeguimiento && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button 
                  className="btn btn-primary"
                  onClick={handleGuardarAccionesSeguimiento}
                  disabled={guardandoAcciones}
                >
                  <Save size={16} />
                  {guardandoAcciones ? 'Guardando...' : 'Guardar Acciones'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Bitácora */}
        <div className="detalle-section bitacora-section">
          <h2 className="section-title">
            <History size={20} />
            Bitácora Automática ({gestion.bitacora?.length || 0})
          </h2>
          
          {gestion.bitacora && gestion.bitacora.length > 0 ? (
            <div className="bitacora-timeline">
              {gestion.bitacora.slice().reverse().map((entrada) => (
                <div key={entrada.id} className="bitacora-entry">
                  <div className="bitacora-dot"></div>
                  <div className="bitacora-content">
                    <div className="bitacora-header">
                      <strong>{entrada.accion}</strong>
                      <span className="bitacora-time">
                        {new Date(entrada.fecha).toLocaleString('es-ES')}
                      </span>
                    </div>
                    {entrada.detalles && (
                      <div className="bitacora-details">{entrada.detalles}</div>
                    )}
                    <div className="bitacora-user">{entrada.usuario}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '1rem' }}>
              No hay entradas en la bitácora
            </p>
          )}
        </div>
      </div>

      {/* Modal para ver calificación de la gestión */}
      {showVerCalificacionModal && gestion.calificacion_instancia && (
        <div className="modal-overlay" onClick={() => setShowVerCalificacionModal(false)}>
          <div className="modal-compartir" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)', color: 'white' }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                ⭐ Calificación de Instancia de Probidad
              </h3>
              <button className="btn-cerrar" onClick={() => setShowVerCalificacionModal(false)} style={{ color: 'white' }}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '1.5rem' }}>
              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '3rem', color: '#FFB800', marginBottom: '0.5rem' }}>
                  {'★'.repeat(gestion.calificacion_instancia.puntuacion)}
                  {'☆'.repeat(5 - gestion.calificacion_instancia.puntuacion)}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#333' }}>
                  {gestion.calificacion_instancia.puntuacion} de 5 estrellas
                </div>
              </div>
              
              <div style={{ background: '#f5f5f5', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>Institución:</strong> {gestion.institucion}
                </div>
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>Asesor de Probidad:</strong> {gestion.asesor_probidad_nombre || 'No especificado'}
                </div>
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>Producto Obtenido:</strong>{' '}
                  <span style={{ 
                    background: '#E8F5E9', 
                    color: '#2E7D32', 
                    padding: '0.25rem 0.5rem', 
                    borderRadius: '4px',
                    fontWeight: 600
                  }}>
                    {gestion.producto_obtenido || 'No especificado'}
                  </span>
                </div>
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>Fecha de Calificación:</strong> {new Date(gestion.calificacion_instancia.fecha).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
                <div>
                  <strong>Calificado por:</strong> {gestion.calificacion_instancia.usuario}
                </div>
              </div>
              
              {gestion.calificacion_instancia.comentario && (
                <div>
                  <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Comentario:</strong>
                  <div style={{ 
                    background: '#FFF8E1', 
                    border: '1px solid #FFC107',
                    borderRadius: '8px', 
                    padding: '1rem',
                    fontStyle: 'italic',
                    lineHeight: 1.6
                  }}>
                    "{gestion.calificacion_instancia.comentario}"
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowVerCalificacionModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Prórroga usando Portal */}
      <ModalPortal isOpen={showProrrogaModal} onClose={() => setShowProrrogaModal(false)}>
        <div 
          className="modal-content"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: '450px' }}
        >
          <div className="modal-header" style={{ background: 'linear-gradient(135deg, #2196F3, #1976D2)', color: 'white' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
              <Clock size={20} /> Otorgar Prórroga
            </h3>
          </div>
          <div className="modal-body">
            <p style={{ color: '#666', marginBottom: '1rem' }}>
              Otorgue días adicionales al plazo de la instancia de probidad para completar el análisis.
            </p>
            
            <div className="form-field" style={{ marginBottom: '1rem' }}>
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
                Días de Prórroga *
              </label>
              <input
                type="number"
                value={diasProrroga}
                onChange={(e) => setDiasProrroga(e.target.value)}
                min="1"
                max="90"
                style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
              />
            </div>
            
            <div className="form-field">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
                Motivo de la Prórroga *
              </label>
              <textarea
                value={motivoProrroga}
                onChange={(e) => setMotivoProrroga(e.target.value)}
                rows={3}
                placeholder="Explique brevemente el motivo de la prórroga..."
                style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', resize: 'vertical' }}
              />
            </div>

            {gestion?.fecha_vencimiento_probidad && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#E3F2FD', borderRadius: '6px', fontSize: '0.9rem' }}>
                <strong>Fecha actual de vencimiento:</strong>{' '}
                {formatLocalDate(gestion.fecha_vencimiento_probidad)}
                <br />
                <strong>Nueva fecha de vencimiento:</strong>{' '}
                {(() => {
                  const fecha = parseLocalDate(gestion.fecha_vencimiento_probidad);
                  if (!fecha) return 'Fecha inválida';
                  fecha.setDate(fecha.getDate() + parseInt(diasProrroga || 0));
                  const year = fecha.getFullYear();
                  const month = String(fecha.getMonth() + 1).padStart(2, '0');
                  const day = String(fecha.getDate()).padStart(2, '0');
                  return formatLocalDate(`${year}-${month}-${day}`);
                })()}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowProrrogaModal(false)}
            >
              Cancelar
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleOtorgarProrroga}
              disabled={!diasProrroga || !motivoProrroga.trim()}
            >
              Otorgar Prórroga
            </button>
          </div>
        </div>
      </ModalPortal>

      {/* Modal de Editar Prórroga */}
      <ModalPortal isOpen={showEditProrrogaModal} onClose={() => setShowEditProrrogaModal(false)}>
        <div 
          className="modal-content"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: '450px' }}
        >
          <div className="modal-header" style={{ background: 'linear-gradient(135deg, #FF9800, #F57C00)', color: 'white' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
              <Pencil size={20} /> Editar Prórroga
            </h3>
          </div>
          <div className="modal-body">
            <p style={{ color: '#666', marginBottom: '1rem' }}>
              Modifique los días y motivo de la prórroga otorgada. La fecha de vencimiento se recalculará desde la fecha original.
            </p>
            
            <div className="form-field" style={{ marginBottom: '1rem' }}>
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
                Días de Prórroga *
              </label>
              <input
                data-testid="edit-prorroga-dias"
                type="number"
                value={editDiasProrroga}
                onChange={(e) => setEditDiasProrroga(e.target.value)}
                min="1"
                max="90"
                style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
              />
            </div>
            
            <div className="form-field">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
                Motivo de la Prórroga *
              </label>
              <textarea
                data-testid="edit-prorroga-motivo"
                value={editMotivoProrroga}
                onChange={(e) => setEditMotivoProrroga(e.target.value)}
                rows={3}
                placeholder="Explique brevemente el motivo de la prórroga..."
                style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', resize: 'vertical' }}
              />
            </div>

            {gestion?.fecha_vencimiento_probidad_original && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#FFF3E0', borderRadius: '6px', fontSize: '0.9rem' }}>
                <strong>Fecha original de vencimiento:</strong>{' '}
                {formatLocalDate(gestion.fecha_vencimiento_probidad_original)}
                <br />
                <strong>Prórroga anterior:</strong>{' '}
                {gestion.prorroga_probidad} días
                <br />
                <strong>Nueva fecha con {editDiasProrroga || 0} días:</strong>{' '}
                {(() => {
                  const fecha = parseLocalDate(gestion.fecha_vencimiento_probidad_original);
                  if (!fecha) return 'Fecha inválida';
                  fecha.setDate(fecha.getDate() + parseInt(editDiasProrroga || 0));
                  const year = fecha.getFullYear();
                  const month = String(fecha.getMonth() + 1).padStart(2, '0');
                  const day = String(fecha.getDate()).padStart(2, '0');
                  return formatLocalDate(`${year}-${month}-${day}`);
                })()}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowEditProrrogaModal(false)}
            >
              Cancelar
            </button>
            <button 
              className="btn btn-primary" 
              data-testid="btn-confirmar-edit-prorroga"
              onClick={handleEditarProrroga}
              disabled={!editDiasProrroga || !editMotivoProrroga.trim()}
              style={{ background: '#F57C00' }}
            >
              Guardar Cambios
            </button>
          </div>
        </div>
      </ModalPortal>

      {/* Modal de Marcar Improcedente */}
      <ModalPortal isOpen={showImprocedeneModal} onClose={() => setShowImprocedenteModal(false)}>
        <div 
          className="modal-content"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: '450px' }}
        >
          <div className="modal-header" style={{ background: 'linear-gradient(135deg, #D32F2F, #B71C1C)', color: 'white' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
              <X size={20} /> Marcar como Improcedente
            </h3>
          </div>
          <div className="modal-body">
            <div style={{ 
              background: '#FFEBEE', 
              border: '1px solid #FFCDD2', 
              borderRadius: '8px', 
              padding: '1rem', 
              marginBottom: '1rem'
            }}>
              <p style={{ margin: 0, color: '#C62828', fontWeight: 500 }}>
                ⚠️ Esta acción marcará la gestión como <strong>IMPROCEDENTE</strong> y la archivará automáticamente.
              </p>
            </div>
            
            <div className="form-field">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
                Motivo de Improcedencia *
              </label>
              <textarea
                value={motivoImprocedente}
                onChange={(e) => setMotivoImprocedente(e.target.value)}
                rows={4}
                placeholder="Explique el motivo por el cual esta gestión es improcedente..."
                style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', resize: 'vertical' }}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setShowImprocedenteModal(false);
                setMotivoImprocedente('');
              }}
            >
              Cancelar
            </button>
            <button 
              className="btn"
              style={{ 
                background: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)',
                color: 'white',
                border: 'none'
              }}
              onClick={handleMarcarImprocedente}
              disabled={!motivoImprocedente.trim()}
            >
              <X size={16} />
              Confirmar Improcedente
            </button>
          </div>
        </div>
      </ModalPortal>
    </div>
  );
};

export default DetalleGestion;
